import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { STAGE_BY_KEY } from '../components/DealStageSelect';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import {
  BarChart3, DollarSign, Clock, Hash, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Filter, Loader2, LayoutDashboard, Download,
  BookOpen, HelpCircle, Sparkles, RefreshCw, Users, Check, AlertTriangle, Send,
  SlidersHorizontal, Eye, FlaskConical
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie, LabelList, LineChart, Line, ComposedChart
} from 'recharts';
import SectionGuide, { resetGuides } from '../components/SectionGuide';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getCurrencySymbol } from '../lib/currency';
import { CurrencyModeToggle, useCurrencyMode } from '../components/insights/CurrencyMode';

import EmailShareDialog from '../components/EmailShareDialog';
import { DealSummaryDialog } from '../components/insights/DealSummaryDialog';
import { ScopePackInsightsCard } from '../components/insights/ScopePackInsightsCard';
import { SalesPipelineInsightsCard } from '../components/insights/SalesPipelineInsightsCard';
import { CSPipelineInsightsCard } from '../components/insights/CSPipelineInsightsCard';
import { ChangePatternsInsightsCard } from '../components/insights/ChangePatternsInsightsCard';
import { RfpInsightsDashboard } from '../components/insights/RfpInsightsDashboard';
import { LibraryInsightsDashboard } from '../components/insights/LibraryInsightsDashboard';
import MarginInsightsTab from '../components/insights/MarginInsightsTab';
import DealOverviewTab from '../components/insights/DealOverviewTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  PM: 'Project Manager', CS: 'Consultant', CF: 'Config Specialist',
  IF: 'Infrastructure', IN: 'Integrations', DM: 'Data Migration', TR: 'Training',
};

const CHART_COLORS = ['#0F2040', '#14B8A6', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6', '#06B6D4', '#F97316'];
const ROLE_COLORS = { PM: '#3B82F6', CS: '#8B5CF6', CF: '#F59E0B', IF: '#10B981', IN: '#6366F1', DM: '#F43F5E', TR: '#06B6D4' };

const TABS = [
  { id: 'deal-overview', label: 'Deal Overview', leadershipOnly: true },
  { id: 'margin', label: 'Margin', leadershipOnly: true },
  { id: 'estimates', label: 'Estimates Dashboard' },
  { id: 'rnd', label: 'R&D', leadershipOnly: true },
  { id: 'comparisons', label: 'Comparisons Dashboard' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'rfp', label: 'RFP Insights' },
  { id: 'library', label: 'Library Insights' },
];

const GROUP_OPTIONS = [
  { value: 'implementation_type', label: 'By Type' },
  { value: 'segment', label: 'By Segment' },
  { value: 'organization', label: 'By Organization' },
  { value: 'industry', label: 'By Industry' },
  { value: 'size', label: 'By Size' },
  { value: 'complexity', label: 'By Complexity' },
  { value: 'sales_rep', label: 'By Sales Rep' },
  { value: 'entity', label: 'By Entity' },
  { value: 'region', label: 'By Region' },
];

const APPROVER_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'cro', label: 'CRO' },
  { value: 'cco', label: 'CCO' },
  { value: 'super_admin', label: 'Super Admin' },
];

const GuardrailEditor = () => {
  const [threshold, setThreshold] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [reminderDays, setReminderDays] = useState('3');
  const [riskTighten, setRiskTighten] = useState('5');
  const [slaTarget, setSlaTarget] = useState('3');
  const [levelsOpen, setLevelsOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/estimates/discount-guardrail`, { withCredentials: true })
      .then(res => {
        setThreshold(String(res.data?.threshold_pct ?? 15));
        setTiers(res.data?.tiers || [{ above_pct: res.data?.threshold_pct ?? 15, roles: ['super_admin'] }]);
        setReminderDays(String(res.data?.reminder_days ?? 3));
        setRiskTighten(String(res.data?.risk_tighten_pct ?? 5));
        setSlaTarget(String(res.data?.sla_target_days ?? 3));
      })
      .catch(() => {});
  }, []);
  if (threshold === null) return null;
  const save = async () => {
    try {
      const res = await axios.put(`${API_URL}/api/estimates/discount-guardrail`,
        { threshold_pct: Number(threshold), tiers, reminder_days: Number(reminderDays) || 3, risk_tighten_pct: Number(riskTighten) || 0, sla_target_days: Number(slaTarget) || 3 }, { withCredentials: true });
      setThreshold(String(res.data.threshold_pct));
      if (res.data.tiers) setTiers(res.data.tiers);
      toast.success(`Guardrail saved — approval needed above ${res.data.threshold_pct}%`);
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the guardrail'); return false; }
  };
  const setTier = (i, patch) => setTiers(ts => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const toggleRole = (i, role) => setTiers(ts => ts.map((t, j) => (j === i
    ? { ...t, roles: t.roles.includes(role) ? t.roles.filter(r => r !== role) : [...t.roles, role] }
    : t)));
  return (
    <span className="flex items-center gap-1.5 text-[10px] text-slate-500" data-testid="discount-guardrail">
      Discount guardrail: approval needed above
      <input value={threshold} onChange={e => setThreshold(e.target.value)}
        className="w-12 h-6 text-[10px] border border-slate-200 rounded-md px-1 text-right bg-white"
        data-testid="discount-guardrail-input" />%
      <button onClick={save} className="text-[#4ABFCF] font-semibold hover:underline" data-testid="discount-guardrail-save">Save</button>
      <span className="text-slate-300">·</span>
      <button onClick={() => setLevelsOpen(true)} className="text-[#0F2040] font-semibold hover:underline"
        data-testid="approval-levels-btn">Approval levels</button>
      <Dialog open={levelsOpen} onOpenChange={setLevelsOpen}>
        <DialogContent className="max-w-xl" data-testid="approval-levels-dialog">
          <DialogHeader><DialogTitle className="text-[#0F2040]">Discount approval levels</DialogTitle></DialogHeader>
          <p className="text-[11px] text-slate-500">
            Who can sign off depends on how deep the discount is — the deepest matching level applies.
            Everything above the {threshold}% guardrail needs sign-off from someone at the right level.
          </p>
          {tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap border border-slate-200 rounded-md p-2.5"
              data-testid={`approval-tier-${i}`}>
              <span className="text-[11px] text-slate-500">Above</span>
              <input value={t.above_pct} onChange={e => setTier(i, { above_pct: e.target.value === '' ? '' : Number(e.target.value) })}
                className="w-14 h-7 text-[11px] border border-slate-200 rounded-md px-1 text-right"
                data-testid={`tier-pct-${i}`} />
              <span className="text-[11px] text-slate-500">% → approved by</span>
              {APPROVER_ROLE_OPTIONS.map(o => (
                <label key={o.value} className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={(t.roles || []).includes(o.value)}
                    onChange={() => toggleRole(i, o.value)} data-testid={`tier-${i}-role-${o.value}`} />
                  {o.label}
                </label>
              ))}
              {tiers.length > 1 && (
                <button onClick={() => setTiers(ts => ts.filter((_, j) => j !== i))}
                  className="text-rose-500 text-[11px] font-semibold hover:underline ml-auto"
                  data-testid={`tier-remove-${i}`}>Remove</button>
              )}
            </div>
          ))}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
              <span className="flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap">
                Remind approvers after
                <input value={reminderDays} onChange={e => setReminderDays(e.target.value)}
                  className="w-10 h-6 text-[11px] border border-slate-200 rounded-md px-1 text-right"
                  data-testid="reminder-days-input" />
                day(s)
              </span>
              <span className="flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap">
                Tighten guardrail by
                <input value={riskTighten} onChange={e => setRiskTighten(e.target.value)}
                  className="w-10 h-6 text-[11px] border border-slate-200 rounded-md px-1 text-right"
                  data-testid="risk-tighten-input" />
                % for risk clients
              </span>
              <span className="flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap">
                Sign-off target
                <input value={slaTarget} onChange={e => setSlaTarget(e.target.value)}
                  className="w-10 h-6 text-[11px] border border-slate-200 rounded-md px-1 text-right"
                  data-testid="sla-target-input" />
                day(s)
              </span>
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => setTiers(ts => [...ts, { above_pct: 30, roles: ['super_admin'] }])}
                disabled={tiers.length >= 4}
                className="text-[11px] font-semibold text-[#4ABFCF] hover:underline disabled:opacity-40 whitespace-nowrap"
                data-testid="tier-add-btn">+ Add level</button>
              <Button size="sm" className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white"
                onClick={async () => { if (await save()) setLevelsOpen(false); }}
                data-testid="approval-levels-save">Save levels</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
};

const TrendArrow = ({ cur, prev, badWhenUp = false }) => {
  if (cur === prev) return <span className="text-slate-400">→</span>;
  const up = cur > prev;
  const good = badWhenUp ? !up : up;
  return <span className={good ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}>{up ? '▲' : '▼'}</span>;
};

const BoardPackButton = () => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!['admin', 'cro', 'cco', 'super_admin'].includes(user?.role)) return null;
  return (
    <button
      onClick={async () => {
        setBusy(true);
        try {
          const res = await axios.get(`${API_URL}/api/insights/board-pack.pdf`,
            { withCredentials: true, responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Seertech-Board-Pack.pdf';
          a.click();
          URL.revokeObjectURL(url);
        } catch (e) { toast.error('Could not build the board pack'); }
        setBusy(false);
      }}
      disabled={busy}
      className="flex items-center gap-1 text-[10px] text-[#4ABFCF] font-semibold hover:underline disabled:opacity-50"
      data-testid="board-pack-btn"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      Board pack (PDF)
    </button>
  );
};

const CoachingEmailButton = () => {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [extraEmails, setExtraEmails] = useState('');
  const [savingRec, setSavingRec] = useState(false);
  if (!['admin', 'super_admin'].includes(user?.role)) return null;
  const openPreview = async () => {
    setPreview(null);
    setPreviewOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/insights/rep-coaching/preview`, { withCredentials: true });
      setPreview(res.data);
      const rec = await axios.get(`${API_URL}/api/insights/rep-coaching/recipients`, { withCredentials: true });
      setExtraEmails((rec.data.extra_emails || []).join(', '));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the preview');
      setPreviewOpen(false);
    }
  };
  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/insights/rep-coaching/send`, {}, { withCredentials: true });
      toast.success(`Coaching recap sent privately to ${res.data.sent} rep${res.data.sent === 1 ? '' : 's'}`);
      setPreviewOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send coaching emails'); }
    setSending(false);
  };
  return (
    <>
      <button onClick={openPreview}
        className="flex items-center gap-1 text-[10px] text-[#4ABFCF] font-semibold hover:underline"
        data-testid="preview-coaching-emails-btn">
        <Eye className="w-3 h-3" />Rep coaching recaps
      </button>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="coaching-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2040]">Rep coaching recaps — {preview?.month || ''}</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-slate-500">Each rep receives only their own recap, privately. Review below, then send.</p>
          {!preview ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" /></div>
          ) : (preview.reps || []).length === 0 ? (
            <p className="text-xs text-slate-400 py-4" data-testid="coaching-preview-empty">No reps with estimates found.</p>
          ) : preview.reps.map(r => (
            <div key={r.rep} className={`border rounded-md p-2.5 text-[11px] ${r.will_send ? 'border-slate-200' : 'border-slate-100 bg-slate-50 opacity-70'}`}
              data-testid={`coaching-preview-${r.rep}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-[#0F2040] text-xs">{r.rep}</span>
                <span className={`text-[10px] ${r.will_send ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {r.will_send ? `→ ${r.email}` : 'no email on file — will be skipped'}
                </span>
              </div>
              <ul className="list-disc pl-4 space-y-0.5 text-slate-600">
                <li>Estimates: <b>{r.count}</b> · total <b>${Math.round(r.total_cost).toLocaleString()}</b> (avg ${Math.round(r.avg_cost).toLocaleString()}) · {Math.round(r.total_effort).toLocaleString()} hrs</li>
                <li>Discounting: {r.discounted_count}/{r.count} deals{r.avg_discount_pct != null ? `, avg ${r.avg_discount_pct}%` : ''}</li>
                <li>Linked RFP outcomes: {r.rfp_won}W / {r.rfp_lost}L{r.rfp_win_rate != null ? ` (${r.rfp_win_rate}% win)` : ''}</li>
                <li>Top deal type: {r.top_type} · top industry: {r.top_industry}</li>
                {r.mom && (
                  <li data-testid={`coaching-trend-${r.rep}`}>
                    Last 30 days vs prior 30:{' '}
                    estimates <TrendArrow cur={r.mom.cur_count} prev={r.mom.prev_count} /> {r.mom.cur_count} (was {r.mom.prev_count})
                    {' · '}value <TrendArrow cur={r.mom.cur_cost} prev={r.mom.prev_cost} /> ${Math.round(r.mom.cur_cost).toLocaleString()} (was ${Math.round(r.mom.prev_cost).toLocaleString()})
                    {(r.mom.cur_avg_discount != null || r.mom.prev_avg_discount != null) && (
                      <> · avg discount <TrendArrow cur={r.mom.cur_avg_discount || 0} prev={r.mom.prev_avg_discount || 0} badWhenUp /> {r.mom.cur_avg_discount || 0}% (was {r.mom.prev_avg_discount || 0}%)</>
                    )}
                  </li>
                )}
              </ul>
            </div>
          ))}
          {preview && (
            <div className="flex items-center gap-2 border-t border-slate-100 pt-2">
              <span className="text-[10px] text-slate-500 whitespace-nowrap">Digest also goes to:</span>
              <input value={extraEmails} onChange={e => setExtraEmails(e.target.value)}
                placeholder="extra@company.com, another@company.com"
                className="flex-1 h-7 text-[11px] border border-slate-200 rounded-md px-2"
                data-testid="digest-recipients-input" />
              <button
                onClick={async () => {
                  setSavingRec(true);
                  try {
                    const res = await axios.put(`${API_URL}/api/insights/rep-coaching/recipients`,
                      { extra_emails: extraEmails.split(',').map(s => s.trim()).filter(Boolean) },
                      { withCredentials: true });
                    setExtraEmails((res.data.extra_emails || []).join(', '));
                    toast.success('Digest recipients saved');
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save recipients'); }
                  setSavingRec(false);
                }}
                disabled={savingRec}
                className="text-[10px] font-semibold text-[#4ABFCF] hover:underline disabled:opacity-40"
                data-testid="digest-recipients-save">Save</button>
            </div>
          )}
          {preview && (preview.reps || []).some(r => r.will_send) && (
            <div className="flex justify-end">
              <Button size="sm" className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white"
                onClick={send} disabled={sending} data-testid="send-coaching-emails-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send now
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const DiscountSlaCard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isApprover = ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role);
  useEffect(() => {
    if (!isApprover) return;
    axios.get(`${API_URL}/api/estimates/discount-sla`, { withCredentials: true })
      .then(res => setData(res.data)).catch(() => setData(null));
  }, [isApprover]);
  if (!isApprover || !data || (data.decided.count === 0 && data.pending.count === 0)) return null;
  const fmt = d => (d == null ? '—' : `${d}d`);
  const target = data.sla_target_days ?? 3;
  const vsTarget = d => (d == null ? 'text-[#0F2040]' : (d <= target ? 'text-emerald-600' : 'text-rose-600'));
  return (
    <Card data-testid="discount-sla-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#4ABFCF]" />
          Discount Sign-off SLA
          <span className="text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-[#4ABFCF]/10 text-[#0F2040]" data-testid="sla-target-chip">
            target {target}d
          </span>
          <span className="text-[10px] font-normal text-slate-500">— how long approvals take, and where they stall</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
          <div data-testid="sla-avg-decision">
            <p className={`text-lg font-bold ${vsTarget(data.decided.avg_days)}`}>{fmt(data.decided.avg_days)}</p>
            <p className="text-[10px] text-slate-500">avg time to decision ({data.decided.count} decided)</p>
          </div>
          <div data-testid="sla-approved">
            <p className={`text-lg font-bold ${vsTarget(data.decided.approved.avg_days)}`}>{fmt(data.decided.approved.avg_days)}</p>
            <p className="text-[10px] text-slate-500">avg to approval ({data.decided.approved.count})</p>
          </div>
          <div data-testid="sla-rejected">
            <p className={`text-lg font-bold ${vsTarget(data.decided.rejected.avg_days)}`}>{fmt(data.decided.rejected.avg_days)}</p>
            <p className="text-[10px] text-slate-500">avg to rejection ({data.decided.rejected.count})</p>
          </div>
          <div data-testid="sla-pending">
            <p className={`text-lg font-bold ${data.pending.avg_days != null && data.pending.avg_days > target ? 'text-rose-600' : 'text-amber-600'}`}>{data.pending.count}</p>
            <p className="text-[10px] text-slate-500">waiting now{data.pending.avg_days != null ? ` · avg ${data.pending.avg_days}d` : ''}</p>
          </div>
        </div>
        {(data.pending.oldest || []).length > 0 && (
          <div data-testid="sla-stalled">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Stalled longest</p>
            {data.pending.oldest.map(p => (
              <p key={p.id} className="text-[11px] text-slate-600" data-testid={`sla-stalled-${p.id}`}>
                <span className={`font-semibold ${p.days > target ? 'text-rose-600' : 'text-amber-600'}`}>{p.days}d</span>
                {p.days > target && <span className="text-[9px] text-rose-500 font-semibold uppercase ml-1">over target</span>}
                {' '}— {p.name} ({p.org}, {p.discount_pct}%)
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Seertech R&D quarterly budget tracker (leadership only) ──
const quarterLabel = (q) => q.replace('-', ' ');
const recentQuarters = () => {
  const now = new Date();
  let y = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) + 1;
  const out = [];
  for (let i = 0; i < 4; i++) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q === 0) { q = 4; y -= 1; }
  }
  return out;
};

const BudgetBar = ({ label, used, budget, fmt }) => {
  const pct = budget > 0 ? Math.min((used / budget) * 100, 100) : 0;
  const over = budget > 0 && used > budget;
  return (
    <div className="flex-1 min-w-[220px]">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className={over ? 'text-rose-600 font-semibold' : 'text-slate-700'}>
          {fmt(used)}{budget > 0 ? ` / ${fmt(budget)}` : ' (no budget set)'}
          {budget > 0 && ` · ${Math.round((used / budget) * 100)}%`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${over ? 'bg-rose-500' : 'bg-violet-500'}`}
          style={{ width: `${budget > 0 ? pct : 0}%` }}
        />
      </div>
    </div>
  );
};

const RndBudgetCard = () => {
  const { user } = useAuth();
  const isLeader = ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role);
  const [quarter, setQuarter] = useState(recentQuarters()[0]);
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [formHours, setFormHours] = useState('');
  const [formCost, setFormCost] = useState('');
  const [saving, setSaving] = useState(false);
  const [trend, setTrend] = useState([]);
  const [trendMetric, setTrendMetric] = useState('cost');

  const load = useCallback(() => {
    if (!isLeader) return;
    axios.get(`${API_URL}/api/insights/rnd-budget`, { params: { quarter }, withCredentials: true })
      .then(res => setData(res.data)).catch(() => setData(null));
    axios.get(`${API_URL}/api/insights/rnd-budget/trend`, { params: { quarters: 8 }, withCredentials: true })
      .then(res => setTrend(res.data.trend || [])).catch(() => setTrend([]));
  }, [isLeader, quarter]);
  useEffect(() => { load(); }, [load]);

  if (!isLeader || !data) return null;

  const saveBudget = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/insights/rnd-budget`, {
        quarter,
        budget_hours: formHours === '' ? null : Number(formHours),
        budget_cost: formCost === '' ? null : Number(formCost),
      }, { withCredentials: true });
      toast.success(`R&D budget saved for ${quarterLabel(quarter)}`);
      setEditOpen(false);
      load();
    } catch {
      toast.error('Could not save budget');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-violet-200 bg-gradient-to-br from-violet-50/40 to-white" data-testid="rnd-budget-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-violet-600" /> Seertech R&D Budget — {quarterLabel(quarter)}
            <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700">
              {data.estimate_count} project{data.estimate_count === 1 ? '' : 's'}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={quarter}
              onChange={e => setQuarter(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-700"
              data-testid="rnd-budget-quarter-select"
            >
              {recentQuarters().map(q => <option key={q} value={q}>{quarterLabel(q)}</option>)}
            </select>
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={() => {
                setFormHours(data.budget_hours ?? '');
                setFormCost(data.budget_cost ?? '');
                setEditOpen(true);
              }}
              data-testid="rnd-budget-edit-btn"
            >
              Set budget
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-6">
          <BudgetBar label="Hours" used={data.used_hours} budget={data.budget_hours || 0}
            fmt={(v) => `${Math.round(v).toLocaleString()} hrs`} />
          <BudgetBar label="Cost" used={data.used_cost} budget={data.budget_cost || 0}
            fmt={(v) => `$${Math.round(v).toLocaleString()}`} />
        </div>
        {(data.budget_hours > 0 && data.used_hours > data.budget_hours) || (data.budget_cost > 0 && data.used_cost > data.budget_cost) ? (
          <div className="mt-3 text-xs text-rose-600 font-medium" data-testid="rnd-budget-over-warning">
            Over budget — R&D spend this quarter exceeds the set limit.
          </div>
        ) : null}
        {data.budget_set_by && (data.budget_hours != null || data.budget_cost != null) && (
          <div className="mt-2 text-[10px] text-slate-400">Budget set by {data.budget_set_by}</div>
        )}

        {/* Quarter-over-quarter trend — spend vs budget */}
        {trend.some(t => t.used_cost > 0 || t.budget_cost || t.used_hours > 0 || t.budget_hours) && (
          <div className="mt-5" data-testid="rnd-budget-trend">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-600">Quarter-over-quarter trend</span>
              <div className="flex rounded-md overflow-hidden border border-violet-200">
                {[['cost', 'Cost'], ['hours', 'Hours']].map(([m, lbl]) => (
                  <button key={m} type="button" onClick={() => setTrendMetric(m)}
                    className={`px-2.5 py-0.5 text-[10px] font-semibold transition-colors ${trendMetric === m ? 'bg-violet-600 text-white' : 'bg-white text-slate-500 hover:bg-violet-50'}`}
                    data-testid={`rnd-trend-metric-${m}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <ComposedChart data={trend.map(t => ({
                quarter: quarterLabel(t.quarter),
                Spend: trendMetric === 'cost' ? t.used_cost : t.used_hours,
                Budget: trendMetric === 'cost' ? t.budget_cost : t.budget_hours,
              }))} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE9FE" />
                <XAxis dataKey="quarter" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }}
                  tickFormatter={(v) => trendMetric === 'cost' ? fmtAxisMoney(v) : v.toLocaleString()} />
                <Tooltip formatter={(v, name) => [
                  trendMetric === 'cost' ? `$${Math.round(v).toLocaleString()}` : `${Math.round(v).toLocaleString()} hrs`,
                  name,
                ]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Spend" fill="#8B5CF6" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Line dataKey="Budget" stroke="#0F2040" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm" data-testid="rnd-budget-dialog">
          <DialogHeader>
            <DialogTitle>Set R&D budget — {quarterLabel(quarter)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Budget hours</Label>
              <input
                type="number" min="0" value={formHours}
                onChange={e => setFormHours(e.target.value)}
                className="w-full mt-1 text-sm border border-slate-200 rounded-md px-3 py-2"
                placeholder="e.g. 2000"
                data-testid="rnd-budget-hours-input"
              />
            </div>
            <div>
              <Label className="text-xs">Budget cost ($)</Label>
              <input
                type="number" min="0" value={formCost}
                onChange={e => setFormCost(e.target.value)}
                className="w-full mt-1 text-sm border border-slate-200 rounded-md px-3 py-2"
                placeholder="e.g. 250000"
                data-testid="rnd-budget-cost-input"
              />
            </div>
            <Button
              onClick={saveBudget} disabled={saving}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="rnd-budget-save-btn"
            >
              {saving ? 'Saving…' : 'Save budget'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const RND_STACK_COLORS = ['#8B5CF6', '#4ABFCF', '#F59E0B', '#10B981', '#E11D48', '#6366F1', '#0EA5E9', '#94A3B8'];
const RND_SEG_COLORS = { 'Implementation': '#0F2040', 'Customer Project': '#F59E0B', 'Seertech R&D': '#8B5CF6' };
const fmtMoney = (v) => `A$${Math.round(v || 0).toLocaleString()}`;
const fmtAxisMoney = (v) => v >= 1000000 ? `A$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `A$${Math.round(v / 1000)}k` : `A$${v}`;

const RndDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState({ key: 'created_at', dir: -1 });
  const [exporting, setExporting] = useState(false);
  const [projectsOnly, setProjectsOnly] = useState(false);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [catEditOpen, setCatEditOpen] = useState(false);
  const [catText, setCatText] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [catBudgetOpen, setCatBudgetOpen] = useState(false);
  const [catBudgetCats, setCatBudgetCats] = useState([]);
  const [catBudgets, setCatBudgets] = useState({});
  const [catBudgetQuarter, setCatBudgetQuarter] = useState('');
  const [catBudgetSaving, setCatBudgetSaving] = useState(false);
  const { user: rndUser } = useAuth();

  const openDrill = (quarter) => {
    if (!quarter) return;
    setDrillLoading(true);
    setDrill({ quarter, rows: [], months: [] });
    axios.get(`${API_URL}/api/insights/rnd-quarter/${quarter}`, { withCredentials: true })
      .then(res => setDrill(res.data))
      .catch(() => { toast.error('Could not load quarter detail'); setDrill(null); })
      .finally(() => setDrillLoading(false));
  };

  const saveCategories = async () => {
    setCatSaving(true);
    try {
      const cats = catText.split('\n').map(c => c.trim()).filter(Boolean);
      await axios.put(`${API_URL}/api/rnd-categories`, { categories: cats }, { withCredentials: true });
      toast.success('R&D categories updated');
      setCatEditOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save categories');
    } finally {
      setCatSaving(false);
    }
  };
  const navigate = useNavigate();

  const loadDashboard = useCallback(() => {
    axios.get(`${API_URL}/api/insights/rnd-dashboard`, { withCredentials: true })
      .then(res => setData(res.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const openCatBudgets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/insights/rnd-category-budgets`, { withCredentials: true });
      setCatBudgetCats(res.data.categories || []);
      setCatBudgets(res.data.budgets || {});
      setCatBudgetQuarter(res.data.quarter);
      setCatBudgetOpen(true);
    } catch {
      toast.error('Could not load category budgets');
    }
  };

  const saveCatBudgets = async () => {
    setCatBudgetSaving(true);
    try {
      const budgets = {};
      catBudgetCats.forEach(c => {
        const v = parseFloat(catBudgets[c]);
        budgets[c] = Number.isFinite(v) && v > 0 ? v : null;
      });
      await axios.put(`${API_URL}/api/insights/rnd-category-budgets`,
        { quarter: catBudgetQuarter, budgets }, { withCredentials: true });
      toast.success('Category budgets saved');
      setCatBudgetOpen(false);
      loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save category budgets');
    } finally {
      setCatBudgetSaving(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/insights/rnd-dashboard.pdf`, { responseType: 'blob', withCredentials: true });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Seertech-RnD-Insights_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-500 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading R&D insights…</div>;
  if (!data) return <div className="text-sm text-slate-500 py-10">R&D insights are available to leadership only.</div>;
  const { kpis, segment_quarters, share, timeline, timeline_projects, projects, category_breakdown, category_budget_status } = data;
  const catBudgetRows = category_budget_status?.rows || [];
  const catHotCount = catBudgetRows.filter(r => r.hot || r.over).length;
  const catOverAny = catBudgetRows.some(r => r.over);

  const sorted = [...projects].sort((a, b) => {
    const av = a[sortBy.key], bv = b[sortBy.key];
    return (av > bv ? 1 : av < bv ? -1 : 0) * sortBy.dir;
  });
  const sortCol = (key) => setSortBy(prev => ({ key, dir: prev.key === key ? -prev.dir : -1 }));
  const budgetBits = [];
  if (kpis.budget_hours) budgetBits.push(`${Math.round(kpis.used_hours).toLocaleString()}/${Math.round(kpis.budget_hours).toLocaleString()} hrs (${Math.round(kpis.used_hours / kpis.budget_hours * 100)}%)`);
  if (kpis.budget_cost) budgetBits.push(`${fmtMoney(kpis.used_cost)}/${fmtMoney(kpis.budget_cost)} (${Math.round(kpis.used_cost / kpis.budget_cost * 100)}%)`);

  return (
    <div className="space-y-5" data-testid="rnd-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-violet-600" /> Seertech R&D — last 8 quarters
        </h2>
        <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting}
          className="h-8 text-xs border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="rnd-export-pdf-btn">
          <Download className="w-3.5 h-3.5 mr-1" /> {exporting ? 'Exporting…' : 'Export PDF'}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="rnd-kpis">
        <KpiCard icon={Hash} label="R&D Projects" value={kpis.count} />
        <KpiCard icon={Clock} label="Total Hours" value={Math.round(kpis.total_hours).toLocaleString()} />
        <KpiCard icon={DollarSign} label="Total Spend" value={fmtMoney(kpis.total_cost)} />
        <KpiCard icon={BarChart3} label="Avg Project" value={fmtMoney(kpis.avg_cost)} />
        <KpiCard icon={TrendingUp} label="Share of Portfolio" value={`${kpis.rnd_share_pct}%`} sub="of all estimate spend" />
        <KpiCard icon={FlaskConical} label={`${kpis.current_quarter} Budget`} value={budgetBits.length ? '' : 'Not set'} sub={budgetBits.join(' · ') || 'Set it on the Estimates tab'} />
      </div>

      {/* Run-rate forecast to quarter end */}
      {kpis.used_cost > 0 && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${
            (kpis.overshoot_hours || kpis.overshoot_cost)
              ? 'border-rose-300 bg-rose-50 text-rose-800'
              : 'border-violet-200 bg-violet-50/50 text-slate-700'
          }`}
          data-testid="rnd-forecast-banner"
        >
          <span className="font-semibold">
            {(kpis.overshoot_hours || kpis.overshoot_cost) ? 'Projected over budget' : 'Quarter-end forecast'}
          </span>
          <span>
            At the current run-rate ({kpis.quarter_elapsed_pct}% of {kpis.current_quarter} elapsed), R&D will reach{' '}
            <span className="font-semibold">{Math.round(kpis.forecast_hours).toLocaleString()} hrs</span> ·{' '}
            <span className="font-semibold">{fmtMoney(kpis.forecast_cost)}</span> by quarter end
            {kpis.budget_cost ? <> vs a {fmtMoney(kpis.budget_cost)} budget ({Math.round(kpis.forecast_cost / kpis.budget_cost * 100)}%)</> : null}
            {!kpis.budget_cost && kpis.budget_hours ? <> vs a {Math.round(kpis.budget_hours).toLocaleString()} hr budget ({Math.round(kpis.forecast_hours / kpis.budget_hours * 100)}%)</> : null}.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm font-semibold text-[#0F2040]">Spend by segment — R&D vs Customer Projects vs Implementations</CardTitle>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={projectsOnly} onChange={e => setProjectsOnly(e.target.checked)}
                  className="accent-violet-600" data-testid="rnd-projects-only-toggle" />
                Projects only
              </label>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={segment_quarters} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="quarter" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtAxisMoney} />
                <Tooltip formatter={(v, n) => [fmtMoney(v), n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {Object.entries(RND_SEG_COLORS)
                  .filter(([seg]) => !projectsOnly || seg !== 'Implementation')
                  .map(([seg, color]) => (
                    <Bar key={seg} dataKey={seg} fill={color} radius={[3, 3, 0, 0]} maxBarSize={22} />
                  ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#0F2040]">Share of spend (window)</CardTitle></CardHeader>
          <CardContent>
            {share.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={share} dataKey="cost" nameKey="segment" innerRadius={55} outerRadius={85} paddingAngle={2}>
                    {share.map((s) => <Cell key={s.segment} fill={RND_SEG_COLORS[s.segment] || '#94A3B8'} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [fmtMoney(v), n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-xs text-slate-400 py-10 text-center">No spend in the window yet.</div>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#0F2040]">R&D spend timeline by project — budget overlaid <span className="text-[10px] font-normal text-slate-400 ml-1">click a quarter for month-by-month detail</span></CardTitle></CardHeader>
        <CardContent>
          {timeline_projects.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={timeline} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                onClick={(st) => st?.activeLabel && openDrill(st.activeLabel)} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE9FE" />
                <XAxis dataKey="quarter" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtAxisMoney} />
                <Tooltip formatter={(v, n) => [fmtMoney(v), n]} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {timeline_projects.map((name, i) => (
                  <Bar key={name} dataKey={name} stackId="rnd" fill={RND_STACK_COLORS[i % RND_STACK_COLORS.length]} maxBarSize={40} />
                ))}
                <Line dataKey="budget_cost" name="Budget" stroke="#0F2040" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div className="text-xs text-slate-400 py-10 text-center">No R&D projects in the window yet — create one from the Estimator (Project type, Seertech R&D).</div>}
        </CardContent>
      </Card>

      {/* Spend by R&D category */}
      <Card data-testid="rnd-category-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm font-semibold text-[#0F2040]">R&D spend by category</CardTitle>
              {catHotCount > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${catOverAny ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}
                  data-testid="rnd-cat-hot-chip">
                  {catHotCount} {catOverAny ? 'need attention' : 'running hot'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={openCatBudgets}
                className="p-1 rounded text-slate-400 hover:text-violet-700 hover:bg-violet-50 transition-colors"
                title="Set quarterly budget per category" data-testid="rnd-cat-budget-btn">
                <DollarSign className="w-3.5 h-3.5" />
              </button>
              {['admin', 'super_admin'].includes(rndUser?.role) && (
                <button type="button"
                  onClick={() => { setCatText(''); axios.get(`${API_URL}/api/rnd-categories`, { withCredentials: true }).then(res => setCatText((res.data.categories || []).join('\n'))); setCatEditOpen(true); }}
                  className="p-1 rounded text-slate-400 hover:text-violet-700 hover:bg-violet-50 transition-colors"
                  title="Edit the category list (admin)" data-testid="rnd-category-edit-btn">
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(category_breakdown || []).length ? (
            <div className="space-y-2">
              {category_breakdown.map(c => {
                const maxCost = category_breakdown[0].cost || 1;
                return (
                  <div key={c.category} className="flex items-center gap-3" data-testid={`rnd-cat-row-${c.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <span className="w-28 text-xs font-medium text-slate-700 truncate">{c.category}</span>
                    <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max((c.cost / maxCost) * 100, 2)}%` }} />
                    </div>
                    <span className="w-44 text-right text-xs text-slate-500">{fmtMoney(c.cost)} · {Math.round(c.hours).toLocaleString()} hrs · {c.count}</span>
                  </div>
                );
              })}
            </div>
          ) : <div className="text-xs text-slate-400 py-4 text-center">No categorised R&D spend yet — set a category on each R&D project in the Estimator.</div>}
          {catBudgetRows.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="rnd-cat-budget-status">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">
                {category_budget_status.quarter} spend vs category budgets
              </div>
              <div className="space-y-2">
                {catBudgetRows.map(r => {
                  const slug = r.category.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  const barColor = r.over ? 'bg-rose-500' : r.hot ? 'bg-amber-500' : 'bg-violet-400';
                  return (
                    <div key={r.category} className="flex items-center gap-3" data-testid={`rnd-cat-budget-row-${slug}`}>
                      <span className="w-28 text-xs font-medium text-slate-700 truncate">{r.category}</span>
                      <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                        {r.budget_cost ? (
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(Math.max(r.pct, 2), 100)}%` }} />
                        ) : null}
                      </div>
                      <span className="w-56 text-right text-xs text-slate-500">
                        {r.budget_cost ? (
                          <>
                            {fmtMoney(r.used_cost)} / {fmtMoney(r.budget_cost)} ({r.pct}%)
                            {r.over ? <Badge className="ml-1.5 text-[9px] px-1 py-0 bg-rose-100 text-rose-700 border-0 align-middle" data-testid={`rnd-cat-over-badge-${slug}`}>Over</Badge>
                              : r.hot ? <Badge className="ml-1.5 text-[9px] px-1 py-0 bg-amber-100 text-amber-700 border-0 align-middle" data-testid={`rnd-cat-hot-badge-${slug}`}>Hot</Badge> : null}
                          </>
                        ) : <>{fmtMoney(r.used_cost)} · no budget</>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={catBudgetOpen} onOpenChange={setCatBudgetOpen}>
        <DialogContent className="max-w-sm" data-testid="rnd-cat-budget-dialog">
          <DialogHeader><DialogTitle>Category budgets — {catBudgetQuarter}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Quarterly spend budget ($) per R&D category. Leave blank for no budget. A category flags "Hot" at 80% and "Over" past 100%.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {catBudgetCats.map(c => (
              <div key={c} className="flex items-center gap-2">
                <span className="w-28 text-xs font-medium text-slate-700 truncate">{c}</span>
                <input type="number" min="0" value={catBudgets[c] ?? ''}
                  onChange={e => setCatBudgets(prev => ({ ...prev, [c]: e.target.value }))}
                  className="flex-1 text-sm border border-slate-200 rounded-md px-3 py-1.5"
                  placeholder="No budget"
                  data-testid={`rnd-cat-budget-input-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
              </div>
            ))}
          </div>
          <Button onClick={saveCatBudgets} disabled={catBudgetSaving}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="rnd-cat-budget-save-btn">
            {catBudgetSaving ? 'Saving…' : 'Save budgets'}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={catEditOpen} onOpenChange={setCatEditOpen}>
        <DialogContent className="max-w-sm" data-testid="rnd-category-dialog">
          <DialogHeader><DialogTitle>Edit R&D categories</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">One category per line. "Other" is always kept as a fallback.</p>
          <textarea value={catText} onChange={e => setCatText(e.target.value)} rows={8}
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 font-mono"
            data-testid="rnd-category-textarea" />
          <Button onClick={saveCategories} disabled={catSaving}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="rnd-category-save-btn">
            {catSaving ? 'Saving…' : 'Save categories'}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-2xl" data-testid="rnd-drill-dialog">
          <DialogHeader><DialogTitle>R&D detail — {drill?.quarter ? quarterLabel(drill.quarter) : ''}</DialogTitle></DialogHeader>
          {drillLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (drill?.rows || []).length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No R&D projects in this quarter.</div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-3 gap-2">
                {(drill.months || []).map(m => (
                  <div key={m.month} className="rounded-lg border border-violet-200 bg-violet-50/40 px-3 py-2" data-testid={`rnd-drill-month-${m.month}`}>
                    <div className="text-xs font-semibold text-[#0F2040]">{m.month_label}</div>
                    <div className="text-[11px] text-slate-600">{fmtMoney(m.cost)} · {Math.round(m.hours).toLocaleString()} hrs · {m.count} project{m.count === 1 ? '' : 's'}</div>
                  </div>
                ))}
              </div>
              <table className="w-full text-xs" data-testid="rnd-drill-table">
                <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-1.5 pr-3">Month</th><th className="py-1.5 pr-3">Project</th><th className="py-1.5 pr-3">Category</th><th className="py-1.5 pr-3">Owner</th><th className="py-1.5 pr-3">Hours</th><th className="py-1.5">Cost</th>
                </tr></thead>
                <tbody>
                  {drill.rows.map(r => (
                    <tr key={`${r.id}-${r.month}`} className="border-b border-slate-100">
                      <td className="py-1.5 pr-3 text-slate-500">{r.month_label}</td>
                      <td className="py-1.5 pr-3 font-medium text-[#0F2040]">{r.name}</td>
                      <td className="py-1.5 pr-3">{r.category}</td>
                      <td className="py-1.5 pr-3">{r.owner}</td>
                      <td className="py-1.5 pr-3">{Math.round(r.hours).toLocaleString()}</td>
                      <td className="py-1.5">{fmtMoney(r.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#0F2040]">R&D projects ({projects.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="rnd-projects-table">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                {[['name', 'Project'], ['category', 'Category'], ['owner', 'Owner'], ['quarter', 'Quarter'], ['hours', 'Hours'], ['cost', 'Cost'], ['status', 'Status']].map(([key, label]) => (
                  <th key={key} className="py-2 pr-4 cursor-pointer select-none hover:text-[#0F2040]"
                    onClick={() => sortCol(key)} data-testid={`rnd-sort-${key}`}>
                    {label}{sortBy.key === key ? (sortBy.dir === -1 ? ' ↓' : ' ↑') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-violet-50/40 cursor-pointer"
                  onClick={() => navigate(`/estimator?load=${p.id}`)} data-testid={`rnd-project-row-${p.id}`}>
                  <td className="py-2 pr-4 font-medium text-[#0F2040]">{p.name}</td>
                  <td className="py-2 pr-4"><Badge variant="outline" className="text-[10px] border-violet-200 text-violet-700">{p.category || 'Other'}</Badge></td>
                  <td className="py-2 pr-4">{p.owner}</td>
                  <td className="py-2 pr-4">{p.quarter}</td>
                  <td className="py-2 pr-4">{Math.round(p.hours).toLocaleString()}</td>
                  <td className="py-2 pr-4">{fmtMoney(p.cost)}</td>
                  <td className="py-2 pr-4"><Badge variant="outline" className="text-[10px]">{p.status}</Badge></td>
                </tr>
              ))}
              {!sorted.length && <tr><td colSpan={7} className="py-6 text-center text-slate-400">No R&D projects yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

const PricingRiskClients = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isLeader = ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role);
  useEffect(() => {
    if (!isLeader) return;
    axios.get(`${API_URL}/api/insights/pricing-risk-clients`, { withCredentials: true })
      .then(res => setData(res.data)).catch(() => setData(null));
  }, [isLeader]);
  if (!isLeader || !data || (data.clients || []).length === 0) return null;
  return (
    <Card className="border-rose-200" data-testid="pricing-risk-clients">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-rose-500" />
          Pricing-Risk Clients
          <span className="text-[10px] font-normal text-slate-500">
            — their discount guardrail is tightened from {data.threshold_pct}% to {data.tightened_to}%
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] text-slate-400 uppercase tracking-wide text-left border-b border-rose-100">
              <th className="py-1.5 pr-2">Client</th>
              <th className="py-1.5 pr-2 text-right">RFP record</th>
              <th className="py-1.5 pr-2 text-right">Lost on pricing</th>
              <th className="py-1.5">Why flagged</th>
            </tr>
          </thead>
          <tbody>
            {data.clients.map(c => (
              <tr key={c.client} className="border-b border-rose-50" data-testid={`risk-client-${c.client}`}>
                <td className="py-1.5 pr-2 font-semibold text-[#0F2040]">{c.client}</td>
                <td className="py-1.5 pr-2 text-right">
                  <span className="text-emerald-600">{c.won}W</span> / <span className="text-rose-600">{c.lost}L</span>
                </td>
                <td className="py-1.5 pr-2 text-right font-semibold text-rose-600">{c.pricing_losses}</td>
                <td className="py-1.5 text-[10px] text-slate-500">
                  {c.pricing_losses} of {c.lost} losses tagged pricing{(c.lost_on_pricing || []).length > 0 ? ` — ${c.lost_on_pricing.join(', ')}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

const REJECT_PRESETS = [
  'Discount too deep for this deal size',
  'Insufficient justification provided',
  'Renegotiate at a lower discount',
  'Client already receives preferential rates',
  'Other',
];

const PendingDiscountApprovals = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectPreset, setRejectPreset] = useState(REJECT_PRESETS[0]);
  const [rejectNote, setRejectNote] = useState('');
  const [busy, setBusy] = useState(false);
  const isApproverRole = ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role);
  const load = useCallback(() => {
    axios.get(`${API_URL}/api/estimates/pending-discount-approvals`, { withCredentials: true })
      .then(res => setData(res.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { if (isApproverRole) load(); }, [isApproverRole, load]);
  if (!isApproverRole || !data || (data.pending || []).length === 0) return null;
  const approve = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/estimates/${id}/approve-discount`, {}, { withCredentials: true });
      toast.success('Discount approved — the estimate can now be marked Approved');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };
  const confirmReject = async () => {
    setBusy(true);
    try {
      const note = rejectPreset === 'Other'
        ? rejectNote.trim()
        : [rejectPreset, rejectNote.trim()].filter(Boolean).join(' — ');
      await axios.post(`${API_URL}/api/estimates/${rejectRow.id}/reject-discount`,
        { note: note || null }, { withCredentials: true });
      toast.success('Discount rejected — the sales lead has been notified');
      setRejectRow(null);
      setRejectNote('');
      setRejectPreset(REJECT_PRESETS[0]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };
  return (
    <Card className="border-amber-300 bg-amber-50/40" data-testid="pending-discount-approvals">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          Discount Approvals Needed
          <span className="text-[10px] font-normal text-slate-500">
            — discounts above {data.threshold_pct}% need sign-off before the estimate can be marked Approved
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] text-slate-400 uppercase tracking-wide text-left border-b border-amber-200/60">
              <th className="py-1.5 pr-2">Estimate</th><th className="py-1.5 pr-2">Client</th>
              <th className="py-1.5 pr-2 text-right">Discount</th><th className="py-1.5 pr-2 text-right">Value</th>
              <th className="py-1.5 pr-2">Sales lead</th><th className="py-1.5 pr-2">Sign-off level</th>
              <th className="py-1.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.pending.map(row => (
              <tr key={row.id} className="border-b border-amber-100/60" data-testid={`pending-discount-${row.id}`}>
                <td className="py-1.5 pr-2 font-semibold text-[#0F2040]">{row.name}
                  {row.rejected && <span className="ml-1.5 text-[9px] text-rose-600 font-semibold uppercase" title={row.rejected_note || ''}>rejected</span>}
                </td>
                <td className="py-1.5 pr-2">{row.org}</td>
                <td className="py-1.5 pr-2 text-right font-semibold text-rose-600">{row.discount_pct}%
                  {row.risk_tightened && <span className="ml-1 text-[8px] font-semibold text-amber-600 uppercase" title="This client is flagged as a pricing risk — the guardrail is tightened for them" data-testid={`risk-tightened-${row.id}`}>risk</span>}
                </td>
                <td className="py-1.5 pr-2 text-right">{row.total_cost != null ? `$${Math.round(row.total_cost).toLocaleString()}` : '—'}</td>
                <td className="py-1.5 pr-2">{row.sales_rep || '—'}</td>
                <td className="py-1.5 pr-2 text-[10px] text-slate-500" data-testid={`required-roles-${row.id}`}>
                  {(row.required_roles || []).join(' / ')}</td>
                <td className="py-1.5 text-right whitespace-nowrap">
                  {row.can_action ? (
                    <>
                      <button onClick={() => approve(row.id)} disabled={busy}
                        className="text-emerald-600 font-semibold hover:underline mr-3 disabled:opacity-40"
                        data-testid={`approve-discount-${row.id}`}>Approve</button>
                      <button onClick={() => setRejectRow(row)} disabled={busy}
                        className="text-rose-600 font-semibold hover:underline disabled:opacity-40"
                        data-testid={`reject-discount-${row.id}`}>Reject</button>
                    </>
                  ) : (
                    <span className="text-[10px] text-slate-400" data-testid={`no-action-${row.id}`}>
                      needs {(row.required_roles || []).join(' / ')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      <Dialog open={!!rejectRow} onOpenChange={(o) => { if (!o) setRejectRow(null); }}>
        <DialogContent className="max-w-md" data-testid="reject-discount-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2040]">
              Reject discount — {rejectRow?.name} ({rejectRow?.discount_pct}%)
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-slate-500">The sales lead is notified privately with your reason. The estimate stays blocked from Approved.</p>
          <div className="space-y-1.5">
            {REJECT_PRESETS.map(p => (
              <label key={p} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="radio" name="reject-preset" checked={rejectPreset === p}
                  onChange={() => setRejectPreset(p)}
                  data-testid={`reject-preset-${REJECT_PRESETS.indexOf(p)}`} />
                {p}
              </label>
            ))}
          </div>
          <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)}
            placeholder={rejectPreset === 'Other' ? 'Describe the reason…' : 'Optional extra context for the sales lead…'}
            rows={2} maxLength={400}
            className="w-full text-xs border border-slate-200 rounded-md p-2 resize-none"
            data-testid="reject-note-input" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setRejectRow(null)}
              data-testid="reject-cancel-btn">Cancel</Button>
            <Button size="sm" className="h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmReject}
              disabled={busy || (rejectPreset === 'Other' && !rejectNote.trim())}
              data-testid="reject-confirm-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Reject discount
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default function InsightsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'estimates');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    org_id: '', industry: '', implementation_type: '', complexity: '', size: '', lifecycle: '', stage: '', workflow: '', sales_rep: '', segment: '',
    exclude_archived: true,
  });
  const [dealDrill, setDealDrill] = useState(null);
  const canSeeDeal = ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role);
  const [groupBy, setGroupBy] = useState('implementation_type');
  const [estimatesData, setEstimatesData] = useState(null);
  const [comparisonsData, setComparisonsData] = useState(null);
  const [approvalsData, setApprovalsData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [guideResetKey, setGuideResetKey] = useState(0);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeStale, setNarrativeStale] = useState(false);
  const [hasAutoGenerated, setHasAutoGenerated] = useState({ estimates: false, comparisons: false });
  // Dashboard-level what-if contingency multiplier (Estimates Dashboard only).
  // Display-only overlay: multiplies cost/effort figures by (1 + pct/100) in
  // the rendered UI so stakeholders can ask "what if every active estimate
  // carried a +10% buffer?" without rebuilding each one. Does NOT modify
  // stored data, exports, or the AI narrative.
  const [dashboardContingencyPct, setDashboardContingencyPct] = useState(0);
  // Unfiltered portfolio blended-rate benchmark — fetched once for the
  // Estimates Dashboard so the Blended Rate KPI can render a "vs portfolio
  // average" chip showing how the **filtered selection** compares to the
  // **whole book of work** (no exclude_estimate_id since this isn't tied
  // to a single estimate).
  const [portfolioBenchmark, setPortfolioBenchmark] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/insights/blended-rate-benchmark`, { withCredentials: true })
      .then((res) => { if (!cancelled) setPortfolioBenchmark(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const projectedEstimatesData = React.useMemo(() => {
    if (!estimatesData || !dashboardContingencyPct) return estimatesData;
    return applyContingencyToEstimatesData(estimatesData, dashboardContingencyPct);
  }, [estimatesData, dashboardContingencyPct]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Load filter options
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/filters`, { withCredentials: true })
      .then(r => setFilters(r.data))
      .catch(() => {});
  }, []);

  // Load dashboard data when tab/filters/groupBy change
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...selectedFilters, group_by: groupBy };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });

      if (activeTab === 'estimates') {
        const res = await axios.get(`${API_URL}/api/insights/estimates`, { params, withCredentials: true });
        setEstimatesData(res.data);
      } else if (activeTab === 'comparisons') {
        const res = await axios.get(`${API_URL}/api/insights/comparisons`, { params, withCredentials: true });
        setComparisonsData(res.data);
      } else if (activeTab === 'approvals') {
        const res = await axios.get(`${API_URL}/api/approval-analytics`, { withCredentials: true });
        setApprovalsData(res.data);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [activeTab, selectedFilters, groupBy]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateFilter = (key, value) => {
    setSelectedFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const exportDashboard = useCallback(async () => {
    setExporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/insights/export`, {
        dashboard: activeTab,
        filters: selectedFilters,
        group_by: groupBy,
        contingency_pct: dashboardContingencyPct || 0,
      }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Insights_${activeTab}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent
    }
    setExporting(false);
  }, [activeTab, selectedFilters, groupBy, dashboardContingencyPct]);

  const generateInsightsPdfBase64 = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/api/insights/export`, {
        dashboard: activeTab,
        filters: selectedFilters,
        group_by: groupBy,
        contingency_pct: dashboardContingencyPct || 0,
      }, { withCredentials: true, responseType: 'blob' });
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(res.data);
      });
    } catch { return null; }
  }, [activeTab, selectedFilters, groupBy, dashboardContingencyPct]);

  const activeFilterCount = Object.values(selectedFilters).filter(Boolean).length;

  const generateNarrative = useCallback(async () => {
    const dashData = activeTab === 'estimates' ? estimatesData : comparisonsData;
    if (!dashData) return;
    setNarrativeLoading(true);
    setNarrativeStale(false);
    try {
      const res = await axios.post(`${API_URL}/api/insights/narrative`, {
        dashboard: activeTab,
        kpis: dashData.kpis || {},
        chart_data: dashData.chart_data || [],
        role_breakdown: dashData.role_breakdown || [],
        active_filters: selectedFilters,
        group_by: groupBy,
        estimate_count: dashData.kpis?.count || 0,
      }, { withCredentials: true });
      setNarrative(res.data.narrative);
      toast.success('AI insights generated');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(`Failed to generate AI insights${e?.response?.status ? ` (HTTP ${e.response.status})` : ''}`, { description: typeof d === 'string' ? d : Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join('; ') : undefined });
    } finally {
      setNarrativeLoading(false);
    }
  }, [activeTab, estimatesData, comparisonsData, selectedFilters, groupBy]);

  // Auto-generate narrative on first data load per tab
  useEffect(() => {
    const dashData = activeTab === 'estimates' ? estimatesData : comparisonsData;
    if (dashData && !hasAutoGenerated[activeTab] && !loading) {
      setHasAutoGenerated(prev => ({ ...prev, [activeTab]: true }));
      generateNarrative();
    }
  }, [activeTab, estimatesData, comparisonsData, loading, hasAutoGenerated, generateNarrative]);

  // Mark narrative as stale when filters/groupBy change (after first generation)
  useEffect(() => {
    if (hasAutoGenerated[activeTab] && narrative) {
      setNarrativeStale(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, groupBy]);

  // Clear narrative when switching tabs
  useEffect(() => {
    if (!hasAutoGenerated[activeTab]) {
      setNarrative(null);
      setNarrativeStale(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F2040]" data-testid="insights-title">Insights</h1>
          <p className="text-sm text-slate-500 mt-0.5">Reports & dashboards across your estimates</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { resetGuides(); setGuideResetKey(k => k + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); toast.success('Guides restored'); }}
            className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 transition-colors px-2 py-1"
            data-testid="reset-guides-btn">
            <HelpCircle className="w-3 h-3" /> Show Guides
          </button>
          {!['rfp', 'library', 'rnd'].includes(activeTab) && (
          <Button onClick={exportDashboard} disabled={exporting || loading}
            className="bg-[#0F2040] hover:bg-[#1A3055] text-white" size="sm"
            data-testid="export-dashboard-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
            Export PDF
          </Button>
          )}
          {!['rfp', 'library', 'rnd'].includes(activeTab) && (
          <EmailShareDialog
            onGeneratePdf={generateInsightsPdfBase64}
            defaultSubject={`Insights Dashboard — ${activeTab === 'estimates' ? 'Estimates' : 'Comparisons'}`}
            defaultFilename={`Insights_${activeTab}_${new Date().toISOString().slice(0, 10)}.pdf`}
            triggerClassName="text-xs"
          />
          )}
        </div>
      </div>

      {/* Insights Overview Guide */}
      <SectionGuide id="insights-overview" title="How the Insights Dashboard Works" accent="#0F2040" icon={BookOpen} resetKey={guideResetKey}>
        <p>
          The <strong>Insights Dashboard</strong> aggregates data across all your saved estimates and comparisons to reveal patterns and benchmarks. Use it to answer questions like: <em>"What's our average cost for a Corporate LMS?"</em> or <em>"How do complex deployments compare to simple ones in effort?"</em>
        </p>
        <p>
          <strong>Two views:</strong> The <em>Estimates Dashboard</em> shows KPIs, cost/effort distributions, and role breakdowns across your portfolio. The <em>Comparisons Dashboard</em> summarizes saved comparison results. Filter by organization, industry, type, complexity, or size — and group the charts to spot trends. Export any view as a branded PDF.
        </p>
      </SectionGuide>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="insights-tabs">
        {TABS.filter(t => !t.leadershipOnly || ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role)).map(tab => (
          <button key={tab.id} onClick={() => switchTab(tab.id)}
            className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-white text-[#0F2040] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filter & Charts Guide */}
      {!['approvals', 'rfp', 'library', 'rnd'].includes(activeTab) && (
      <SectionGuide id="insights-charts" title="Filters & Charts" accent="#14B8A6" resetKey={guideResetKey}>
        <p>
          Use the <strong>filters</strong> to narrow the data — by organization, industry, implementation type, complexity, or project size. The <em>Group by</em> dropdown controls how the charts aggregate (e.g., group by Type to compare Corporate vs. Workforce costs). All KPI cards, bar charts, and pie charts update in real time as you adjust filters.
        </p>
      </SectionGuide>
      )}

      {/* Filter Bar */}
      {!['approvals', 'rfp', 'library', 'rnd'].includes(activeTab) && (
      <Card data-testid="filter-bar">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-semibold text-[#0F2040]">Filters</span>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="text-[10px]">{activeFilterCount} active</Badge>
            )}
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-slate-400">Group by</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="h-7 w-36 text-xs" data-testid="group-by-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <FilterSelect label="Organization" value={selectedFilters.org_id}
              onChange={v => updateFilter('org_id', v)}
              options={(filters?.organizations || []).map(o => ({ value: o.id, label: o.name }))}
              testId="filter-org" />
            <FilterSelect label="Industry" value={selectedFilters.industry}
              onChange={v => updateFilter('industry', v)}
              options={(filters?.industries || []).map(i => ({ value: i, label: i }))}
              testId="filter-industry" />
            <FilterSelect label="Segment" value={selectedFilters.segment}
              onChange={v => updateFilter('segment', v)}
              options={[{ value: 'implementations', label: 'Implementations' },
                        { value: 'projects_customer', label: 'Customer Projects' },
                        { value: 'projects_rnd', label: 'Seertech R&D' }]}
              testId="filter-segment" />
            <FilterSelect label="Type" value={selectedFilters.implementation_type}
              onChange={v => updateFilter('implementation_type', v)}
              options={(filters?.implementation_types || []).map(t => ({ value: t, label: t }))}
              testId="filter-type" />
            <FilterSelect label="Complexity" value={selectedFilters.complexity}
              onChange={v => updateFilter('complexity', v)}
              options={(filters?.complexities || []).map(c => ({ value: c, label: c }))}
              testId="filter-complexity" />
            <div>
              <Label className="text-[10px] text-slate-400 mb-1 block">Archived</Label>
              <button type="button"
                onClick={() => setSelectedFilters(prev => {
                  const next = !prev.exclude_archived;
                  const lc = (prev.lifecycle || '').split(',').filter(Boolean).filter(x => !(next && x === 'archived')).join(',');
                  return { ...prev, exclude_archived: next, lifecycle: lc };
                })}
                data-testid="filter-exclude-archived"
                aria-pressed={selectedFilters.exclude_archived}
                className={`h-8 w-full text-xs rounded-md border px-2 text-left ${selectedFilters.exclude_archived ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200'}`}>
                {selectedFilters.exclude_archived ? 'Excluding archived' : 'Including archived'}
              </button>
            </div>
            <FilterSelect label="Size" value={selectedFilters.size}
              onChange={v => updateFilter('size', v)}
              options={(filters?.sizes || []).map(s => ({ value: s, label: s }))}
              testId="filter-size" />
            {activeTab === 'estimates' && (
              <>
                <div className="col-span-2" data-testid="filter-lifecycle">
                  <Label className="text-[10px] text-slate-400 mb-1 block">Lifecycle <span className="text-slate-300">· multi-select</span></Label>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {(filters?.lifecycles || []).filter(st => !(selectedFilters.exclude_archived && st === 'archived')).map((st) => {
                      const sel = (selectedFilters.lifecycle || '').split(',').filter(Boolean);
                      const on = sel.includes(st);
                      return (
                        <button key={st} type="button"
                          onClick={() => updateFilter('lifecycle', (on ? sel.filter(x => x !== st) : [...sel, st]).join(','))}
                          data-testid={`filter-lifecycle-${st}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                          {st.replace(/_/g, ' ')}
                        </button>
                      );
                    })}
                    {(selectedFilters.lifecycle || '') && (
                      <button type="button" onClick={() => updateFilter('lifecycle', '')} className="text-[10px] text-slate-400 underline px-1" data-testid="filter-lifecycle-clear">clear</button>
                    )}
                  </div>
                </div>
                <div className="col-span-2" data-testid="filter-stage">
                  <Label className="text-[10px] text-slate-400 mb-1 block">Deal stage <span className="text-slate-300">· multi-select</span></Label>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {(filters?.stages || []).map((st) => {
                      const sel = (selectedFilters.stage || '').split(',').filter(Boolean);
                      const on = sel.includes(st.key);
                      return (
                        <button key={st.key} type="button"
                          onClick={() => updateFilter('stage', (on ? sel.filter(x => x !== st.key) : [...sel, st.key]).join(','))}
                          data-testid={`filter-stage-${st.key}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${on ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                          {st.label}
                        </button>
                      );
                    })}
                    {(selectedFilters.stage || '') && (
                      <button type="button" onClick={() => updateFilter('stage', '')} className="text-[10px] text-slate-400 underline px-1" data-testid="filter-stage-clear">clear</button>
                    )}
                  </div>
                </div>
                <FilterSelect label="Workflow" value={selectedFilters.workflow}
                  onChange={v => updateFilter('workflow', v)}
                  options={(filters?.workflows || []).map(s => ({ value: s, label: s.replace(/_/g, ' ') }))}
                  testId="filter-workflow" />
                <FilterSelect label="Sales Rep" value={selectedFilters.sales_rep}
                  onChange={v => updateFilter('sales_rep', v)}
                  options={(filters?.sales_reps || []).map(r => ({ value: r.id, label: r.name }))}
                  testId="filter-sales-rep" />
              </>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {/* Estimates Dashboard */}
      {activeTab === 'estimates' && estimatesData && !loading && (
        <>
          <DashboardContingencyBar
            value={dashboardContingencyPct}
            onChange={setDashboardContingencyPct}
            baselineTotalCost={estimatesData.kpis?.total_cost || 0}
            baselineTotalEffort={estimatesData.kpis?.total_effort || 0}
          />
          <EstimatesDashboard
            data={projectedEstimatesData}
            groupBy={groupBy}
            portfolioBenchmark={portfolioBenchmark}
            contingencyPct={dashboardContingencyPct}
            baselineKpis={estimatesData.kpis}
            onDrill={canSeeDeal ? setDealDrill : null}
            stageFilter={selectedFilters.stage || ''}
            onStageFilter={(v) => updateFilter('stage', v)}
          />
          <DealSummaryDialog estimateId={dealDrill} open={!!dealDrill} onClose={() => setDealDrill(null)} />
          <RndBudgetCard />
          <PricingOutliersCard
            chartData={projectedEstimatesData?.chart_data || []}
            portfolioBenchmark={portfolioBenchmark}
            groupLabel={GROUP_OPTIONS.find(o => o.value === groupBy)?.label || groupBy}
          />
          <SalesPipelineInsightsCard />
          <CSPipelineInsightsCard />
          <ScopePackInsightsCard days={30} />
          <ChangePatternsInsightsCard days={365} minVotes={2} />
          <InsightsNarrativePanel
            narrative={narrative}
            loading={narrativeLoading}
            stale={narrativeStale}
            onGenerate={generateNarrative}
            dashboardType="estimates"
          />
        </>
      )}

      {/* Comparisons Dashboard */}
      {activeTab === 'comparisons' && comparisonsData && !loading && (
        <>
          <ComparisonsDashboard data={comparisonsData} groupBy={groupBy} />
          <InsightsNarrativePanel
            narrative={narrative}
            loading={narrativeLoading}
            stale={narrativeStale}
            onGenerate={generateNarrative}
            dashboardType="comparisons"
          />
        </>
      )}

      {/* Approvals Dashboard */}
      {activeTab === 'approvals' && approvalsData && !loading && (
        <ApprovalsDashboard data={approvalsData} />
      )}

      {/* RFP Insights Dashboard (self-fetching) */}
      {activeTab === 'rnd' && <RndDashboard />}
      {activeTab === 'margin' && <MarginInsightsTab />}
      {activeTab === 'deal-overview' && <DealOverviewTab />}

      {activeTab === 'rfp' && <RfpInsightsDashboard />}

      {/* Library Insights Dashboard (self-fetching) */}
      {activeTab === 'library' && <LibraryInsightsDashboard />}
    </div>
  );
}


function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-[10px] text-slate-400 mb-1 block">{label}</Label>
      <Select value={value || 'all'} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs" data-testid={testId}>
          <SelectValue placeholder={`All ${label}s`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {options.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
/**
 * What-if attribution chip — surfaces *why* a KPI value moved when the
 * Dashboard contingency overlay is active. Renders a small coloured pill
 * directly inside the KPI card's subNode slot:
 *
 *   • pct > 0  → amber  →  "+$3,200 · uncertainty buffer"
 *   • pct < 0  → emerald → "-$1,800 · confidence discount"
 *   • pct = 0  → null    →  no chip rendered
 *
 * Stakeholders can read the cause (slider) and the effect (delta) on
 * the same card without cross-referencing the bar at the top.
 */
function ContingencyAttributionChip({ pct, baseline, projected, kind, testId }) {
  if (!pct || baseline === undefined || baseline === null) return null;
  const delta = (projected ?? 0) - baseline;
  if (Math.abs(delta) < 0.5) return null;

  const sign = delta > 0 ? '+' : '−';
  const abs = Math.round(Math.abs(delta));
  const formatted = kind === 'cost'
    ? `${sign}$${abs.toLocaleString()}`
    : `${sign}${abs.toLocaleString()} hrs`;
  const semantic = pct > 0 ? 'uncertainty buffer' : 'confidence discount';
  const palette = pct > 0
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';

  return (
    <div
      className={`mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-semibold tabular-nums ${palette}`}
      data-testid={testId}
      title={`Dashboard What-if Contingency overlay (${pct > 0 ? '+' : ''}${pct}%) ${pct > 0 ? 'added to' : 'rebated from'} this KPI`}
    >
      <SlidersHorizontal className="w-2.5 h-2.5" />
      <span>{formatted}</span>
      <span className="text-[8px] uppercase tracking-wide opacity-70 font-medium">· {semantic}</span>
    </div>
  );
}





function KpiCard({ icon: Icon, label, value, sub, subNode, trend, color = 'text-[#0F2040]' }) {
  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-4 h-4 text-slate-400" />
          <span className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</span>
        </div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
        {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
        {subNode}
        {trend !== undefined && trend !== null && trend !== 0 && (
          <div className={`flex items-center gap-0.5 mt-1 text-[10px] font-medium ${trend > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
            {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function EstimatesDashboard({ data, groupBy, portfolioBenchmark, contingencyPct = 0, baselineKpis = null, onDrill = null, stageFilter = '', onStageFilter = null }) {
  const [ccyMode, setCcyMode] = useCurrencyMode();
  const rowCost = (e) => (ccyMode === 'native' ? `${getCurrencySymbol(e.currency_code || 'AUD')}${Math.round(e.total_cost || 0).toLocaleString()}` : `A$${Math.round(e.total_cost_aud ?? e.total_cost ?? 0).toLocaleString()}`);
  const { kpis, chart_data, role_breakdown, estimates, status_breakdown, stage_breakdown, rep_breakdown, rep_trends, discount_waterfall, win_rate_trend, segment_breakdown } = data;
  const groupLabel = GROUP_OPTIONS.find(o => o.value === groupBy)?.label || groupBy;

  // Filter-selection blended rate (Σ filtered cost / Σ filtered effort).
  // Compared against the unfiltered `portfolioBenchmark.portfolio_blended_rate`
  // so the chip surfaces the diagnostic delta — e.g. "Healthcare estimates
  // run 12% above portfolio avg" — using aggregations we already compute
  // server-side for `kpis`.
  const filteredBlended = kpis.total_effort > 0 ? kpis.total_cost / kpis.total_effort : 0;

  // Per-group blended-rate delta vs portfolio (avg_cost ÷ avg_effort works
  // because the per-group means share their N denominator, so the ratio
  // equals Σgroup_cost ÷ Σgroup_effort). Rendered as a small +N% chip
  // above each bar in the Cost Distribution chart so leadership can see
  // *where* in the portfolio pricing is mis-aligned at a glance.
  const portfolioBlended = portfolioBenchmark?.portfolio_blended_rate || 0;
  const portfolioSample = portfolioBenchmark?.sample_size || 0;
  const showPerGroupDeltas = portfolioBlended > 0 && portfolioSample >= 3;
  const chartDataWithDeltas = useMemo(() => {
    return (chart_data || []).map((g) => {
      let deltaPct = null;
      if (showPerGroupDeltas && g.avg_effort > 0) {
        const groupBlended = g.avg_cost / g.avg_effort;
        deltaPct = ((groupBlended - portfolioBlended) / portfolioBlended) * 100;
      }
      return { ...g, blended_delta_pct: deltaPct };
    });
  }, [chart_data, portfolioBlended, showPerGroupDeltas]);

  return (
    <div className="space-y-5" data-testid="estimates-dashboard">
      {/* Segment split — Implementations vs Customer Projects vs Seertech R&D */}
      {(segment_breakdown || []).length > 0 && (
        <div className="flex flex-wrap gap-3" data-testid="segment-breakdown">
          {segment_breakdown.map(seg => (
            <div
              key={seg.segment}
              className={`flex items-center gap-3 px-4 py-2 rounded-lg border text-sm ${
                seg.segment === 'Seertech R&D'
                  ? 'border-violet-200 bg-violet-50 text-violet-900'
                  : seg.segment === 'Customer Project'
                    ? 'border-amber-200 bg-amber-50 text-amber-900'
                    : 'border-slate-200 bg-white text-slate-800'
              }`}
              data-testid={`segment-tile-${seg.segment.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <span className="font-semibold">{seg.segment === 'Seertech R&D' || seg.count === 1 ? seg.segment : `${seg.segment}s`}</span>
              <span className="text-xs opacity-70">{seg.count} · A${Math.round(seg.total_cost).toLocaleString()} · {Math.round(seg.total_effort).toLocaleString()} hrs</span>
            </div>
          ))}
        </div>
      )}
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <KpiCard icon={Hash} label="Estimates" value={kpis.count} />
        <KpiCard icon={DollarSign} label="Avg Cost" value={`A$${kpis.avg_cost.toLocaleString()}`}
          sub={`Total: A$${kpis.total_cost.toLocaleString()} (converted)`}
          subNode={
            <ContingencyAttributionChip
              pct={contingencyPct}
              baseline={baselineKpis?.avg_cost}
              projected={kpis.avg_cost}
              kind="cost"
              testId="kpi-avg-cost-attribution"
            />
          } />
        <KpiCard
          icon={SlidersHorizontal}
          label="Blended Rate"
          value={filteredBlended > 0 ? `A$${Math.round(filteredBlended).toLocaleString()}/hr` : '—'}
          subNode={
            <FilteredBlendedRateBenchmarkChip
              filteredBlended={filteredBlended}
              benchmark={portfolioBenchmark}
            />
          }
        />
        <KpiCard icon={Clock} label="Avg Effort" value={`${kpis.avg_effort} hrs`}
          sub={`Total: ${kpis.total_effort.toLocaleString()} hrs`}
          subNode={
            <ContingencyAttributionChip
              pct={contingencyPct}
              baseline={baselineKpis?.avg_effort}
              projected={kpis.avg_effort}
              kind="effort"
              testId="kpi-avg-effort-attribution"
            />
          } />
        <KpiCard icon={DollarSign} label="Total Cost" value={`A$${kpis.total_cost.toLocaleString()}`}
          subNode={
            <ContingencyAttributionChip
              pct={contingencyPct}
              baseline={baselineKpis?.total_cost}
              projected={kpis.total_cost}
              kind="cost"
              testId="kpi-total-cost-attribution"
            />
          } />
        <KpiCard icon={Clock} label="Total Effort" value={`${kpis.total_effort.toLocaleString()} hrs`}
          subNode={
            <ContingencyAttributionChip
              pct={contingencyPct}
              baseline={baselineKpis?.total_effort}
              projected={kpis.total_effort}
              kind="effort"
              testId="kpi-total-effort-attribution"
            />
          } />
      </div>

      {/* Lifecycle & workflow status mix */}
      {(stage_breakdown || []).length > 0 && (
        <Card data-testid="chart-stage-funnel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Deal Stage Funnel <span className="text-[10px] font-normal text-slate-400">· count and probability-weighted value (A$)</span></CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {stage_breakdown.map((g) => {
              const meta = STAGE_BY_KEY[g.stage];
              const sel = (stageFilter || '').split(',').filter(Boolean);
              const on = sel.includes(g.stage);
              const max = Math.max(1, ...stage_breakdown.map(x => x.count));
              return (
                <button key={g.stage} type="button" onClick={() => onStageFilter?.((on ? sel.filter(x => x !== g.stage) : [...sel, g.stage]).join(','))}
                  className={`w-full text-left rounded-md px-2 py-1 hover:bg-slate-50 ${on ? 'ring-1 ring-[#0F2040]/40 bg-slate-50' : ''}`} data-testid={`stage-funnel-${g.stage}`}>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="w-40 shrink-0 font-medium text-slate-700">{g.label}</span>
                    <span className="flex-1 h-3 rounded bg-slate-100 overflow-hidden"><span className={`block h-full rounded ${meta?.bar || 'bg-slate-300'}`} style={{ width: `${Math.max(2, (g.count / max) * 100)}%` }} /></span>
                    <span className="w-16 text-right text-slate-500">{g.count} est.</span>
                    <span className="w-28 text-right text-slate-700">A${Math.round(g.cost_aud).toLocaleString()}</span>
                    <span className="w-28 text-right font-semibold text-[#0F2040]" title="Weighted by stage probability">A${Math.round(g.weighted_cost_aud).toLocaleString()}</span>
                  </div>
                </button>
              );
            })}
            <p className="text-[10px] text-slate-400">Click a stage to filter the dashboard. Estimates with no stage carry 0% weight.</p>
          </CardContent>
        </Card>
      )}
      {(status_breakdown || []).length > 0 && (
        <Card data-testid="chart-status-mix">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Lifecycle &amp; Workflow Mix</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="h-4 rounded-full overflow-hidden flex bg-slate-100">
              {status_breakdown.map((s, i) => (
                <div key={s.status}
                  className={s.kind === 'lifecycle' ? 'bg-emerald-500' : s.kind === 'workflow' ? 'bg-[#4ABFCF]' : 'bg-slate-300'}
                  style={{ width: `${(s.count / kpis.count) * 100}%`, opacity: 1 - (i % 3) * 0.18 }}
                  title={`${s.status.replace(/_/g, ' ')}: ${s.count}`} />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {status_breakdown.map(s => (
                <p key={s.status} className="text-[10px] text-slate-500 flex items-center gap-1.5"
                  data-testid={`status-mix-${s.status}`}>
                  <span className={`w-2 h-2 rounded-full ${s.kind === 'lifecycle' ? 'bg-emerald-500' : s.kind === 'workflow' ? 'bg-[#4ABFCF]' : 'bg-slate-300'}`} />
                  {s.status.replace(/_/g, ' ')}
                  <span className="font-semibold text-[#0F2040]">{s.count}</span>
                  <span className="text-slate-400">({Math.round((s.count / kpis.count) * 100)}%)</span>
                </p>
              ))}
            </div>
            <p className="text-[10px] text-slate-400">Green = lifecycle (confirmed, won, lost, completed, archived) · teal = workflow (draft, in review, approved, changes requested) · grey = not set. Use the Lifecycle / Workflow filters above to drill in.</p>
          </CardContent>
        </Card>
      )}

      {/* Sales rep comparison */}
      {(rep_breakdown || []).length > 0 && (
        <Card data-testid="chart-rep-comparison">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Sales Rep Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] text-slate-400 uppercase tracking-wide text-left border-b border-slate-200">
                    <th className="py-1.5 pr-2">Rep</th><th className="py-1.5 pr-2 text-right">Estimates</th>
                    <th className="py-1.5 pr-2 text-right">Total cost</th><th className="py-1.5 pr-2 text-right">Avg cost</th>
                    <th className="py-1.5 pr-2 text-right">Effort (hrs)</th><th className="py-1.5 pr-2 text-right">Blended rate</th>
                    <th className="py-1.5 pr-2 text-right">Discounted</th><th className="py-1.5 pr-2 text-right">Avg discount</th>
                    <th className="py-1.5 pr-2 text-right" title="Won/lost RFP outcomes for this rep's client organisations">RFP W/L</th>
                    <th className="py-1.5 pr-2">Top type</th><th className="py-1.5">Top industry</th>
                  </tr>
                </thead>
                <tbody>
                  {rep_breakdown.map(r => (
                    <tr key={r.rep} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rep-row-${r.rep}`}>
                      <td className="py-1.5 pr-2 font-semibold text-[#0F2040]">{r.rep}</td>
                      <td className="py-1.5 pr-2 text-right">{r.count}</td>
                      <td className="py-1.5 pr-2 text-right">A${r.total_cost.toLocaleString()}</td>
                      <td className="py-1.5 pr-2 text-right">A${Math.round(r.avg_cost).toLocaleString()}</td>
                      <td className="py-1.5 pr-2 text-right">{Math.round(r.total_effort).toLocaleString()}</td>
                      <td className="py-1.5 pr-2 text-right">{r.blended_rate != null ? `A$${r.blended_rate}/hr` : '—'}</td>
                      <td className="py-1.5 pr-2 text-right">{r.discounted_count}/{r.count}</td>
                      <td className={`py-1.5 pr-2 text-right font-semibold ${r.avg_discount_pct > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                        {r.avg_discount_pct != null ? `${r.avg_discount_pct}%` : '—'}</td>
                      <td className="py-1.5 pr-2 text-right">
                        {r.rfp_won + r.rfp_lost > 0
                          ? `${r.rfp_won}W/${r.rfp_lost}L${r.rfp_win_rate != null ? ` (${r.rfp_win_rate}%)` : ''}`
                          : '—'}</td>
                      <td className="py-1.5 pr-2">{r.top_type}</td>
                      <td className="py-1.5">{r.top_industry}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(rep_trends || []).some(t => t.quarters.length > 1) && (
              <div className="mt-3 pt-2 border-t border-slate-100 space-y-1" data-testid="rep-trends">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Quarter-by-quarter per rep</p>
                {rep_trends.map(t => (
                  <p key={t.rep} className="text-[10px] text-slate-500" data-testid={`rep-trend-${t.rep}`}>
                    <span className="font-semibold text-[#0F2040]">{t.rep}</span>
                    <span className="text-slate-400"> · </span>
                    {t.quarters.map(q => `${q.quarter}: ${q.count} est · A$${Math.round(q.total_cost / 1000)}k${q.avg_discount_pct != null ? ` · ${q.avg_discount_pct}% avg disc` : ''}`).join('  |  ')}
                  </p>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
              <p className="text-[10px] text-slate-400">Who works on what: deal mix, relative size/effort, discounts applied to win, and how those clients' RFPs actually closed. Use the Sales Rep filter or "By Sales Rep" grouping to drill in.</p>
              <div className="flex items-center gap-4">
                <GuardrailEditor />
                <CoachingEmailButton />
                <BoardPackButton />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <PendingDiscountApprovals />

      <DiscountSlaCard />

      <PricingRiskClients />

      {/* Team win-rate trend by quarter */}
      {(win_rate_trend || []).length > 0 && (
        <Card data-testid="chart-win-rate-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Win Rate Trend — team win likelihood by quarter</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={win_rate_trend} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="quarter" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={(v, n, p) => [`${v}% (${p.payload.wins}W / ${p.payload.losses}L)`, 'Win rate']} labelStyle={{ fontWeight: 600 }} />
                <Line type="monotone" dataKey="win_rate" name="Win rate" stroke="#4ABFCF" strokeWidth={2.5}
                  dot={{ r: 4, fill: '#0F2040' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-slate-400 mt-1">
              Closed RFP outcomes plus won/lost estimates, bucketed by the quarter they closed. Team-wide — not affected by the filters above.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Discount waterfall — revenue conceded vs deals won */}
      {(discount_waterfall || []).length > 0 && (
        <Card data-testid="chart-discount-waterfall">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Discount Waterfall — revenue given up vs deals won</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={discount_waterfall} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="quarter" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => (v >= 10000 ? `A$${(v / 1000).toFixed(0)}k` : `A$${Math.round(v).toLocaleString()}`)} />
                <Tooltip formatter={(v, n) => [`$${Number(v).toLocaleString()}`, n]} labelStyle={{ fontWeight: 600 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="given_away" name="Revenue given up to discounts" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                <Bar dataKey="won_value" name="Discounted deal value won (RFP-linked)" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-slate-400 mt-1">
              Per quarter: list-price revenue conceded through rate-card discounts vs the discounted estimate value for clients whose linked RFPs were won. Respects the filters above.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Cost Distribution */}
        <Card data-testid="chart-cost">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">
              Cost Distribution — {groupLabel}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chart_data.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartDataWithDeltas} margin={{ top: 22, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `A$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v, name, props) => {
                      const pct = props?.payload?.blended_delta_pct;
                      const pctStr = pct === null || pct === undefined
                        ? null
                        : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs portfolio avg`;
                      return [`$${Number(v).toLocaleString()}`, pctStr || ''];
                    }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Bar dataKey="avg_cost" name="Avg Cost" radius={[4, 4, 0, 0]}>
                    {chartDataWithDeltas.map((d) => <Cell key={`cost-${d.name}`} fill={CHART_COLORS[chartDataWithDeltas.indexOf(d) % CHART_COLORS.length]} />)}
                    {showPerGroupDeltas && (
                      <LabelList
                        dataKey="blended_delta_pct"
                        position="top"
                        content={renderBlendedDeltaLabel}
                      />
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        {/* Effort Distribution */}
        <Card data-testid="chart-effort">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">
              Effort Distribution — {groupLabel}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chart_data.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chart_data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}h`} />
                  <Tooltip formatter={(v) => [`${Number(v).toLocaleString()} hrs`, '']} labelStyle={{ fontWeight: 600 }} />
                  <Bar dataKey="avg_effort" name="Avg Effort" radius={[4, 4, 0, 0]}>
                    {chart_data.map((d, i) => <Cell key={`effort-${d.name}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>
      </div>

      {/* Role Breakdown + Effort by Role per Group */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Role Pie */}
        <Card data-testid="chart-roles">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Effort by Resource Type</CardTitle>
          </CardHeader>
          <CardContent>
            {role_breakdown.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie data={role_breakdown.map(r => ({
                      name: ROLE_LABELS[r.role] || r.role,
                      value: r.hours,
                      fill: ROLE_COLORS[r.role] || '#94A3B8',
                    }))} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={80} innerRadius={40} strokeWidth={2} />
                    <Tooltip formatter={(v) => [`${v} hrs`, '']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 flex-1">
                  {role_breakdown.map(r => (
                    <div key={r.role} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm shrink-0"
                        style={{ backgroundColor: ROLE_COLORS[r.role] || '#94A3B8' }} />
                      <span className="text-[10px] text-slate-600 flex-1">{ROLE_LABELS[r.role] || r.role}</span>
                      <span className="text-[10px] font-semibold text-[#0F2040]">{r.hours} hrs</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        {/* Stacked Effort by Role per Group */}
        <Card data-testid="chart-effort-stacked">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">
              Effort by Role — {groupLabel}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chart_data.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chart_data.map(g => {
                  const entry = { name: g.name };
                  Object.entries(g.effort_by_role || {}).forEach(([r, h]) => {
                    entry[ROLE_LABELS[r] || r] = h;
                  });
                  return entry;
                })} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {Object.keys(ROLE_COLORS).map(role => (
                    <Bar key={role} dataKey={ROLE_LABELS[role]} stackId="roles"
                      fill={ROLE_COLORS[role]} radius={0} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>
      </div>

      {/* Estimates Table */}
      <Card data-testid="estimates-table">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">
              Estimate Details ({estimates.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <CurrencyModeToggle mode={ccyMode} onChange={setCcyMode} testId="est-ccy-mode" />
              <span className="text-[10px] text-slate-400">{ccyMode === 'native' ? 'Rows in each estimate\'s currency · KPIs and charts stay in A$' : 'All amounts in A$ at saved FX'}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Estimate</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Organization</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Type</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Complexity</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600">Effort</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Cost{ccyMode === 'native' ? ' (estimate ccy)' : ' (A$)'}</th>
                </tr>
              </thead>
              <tbody>
                {estimates.slice(0, 50).map((e, i) => (
                  <tr key={e.id || i} className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${onDrill && e.id ? 'cursor-pointer' : ''}`}
                    onClick={() => { if (onDrill && e.id) onDrill(e.id); }}
                    title={onDrill && e.id ? 'Open deal summary — effort · cost · price · margin' : undefined}
                    data-testid={`est-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium text-[#0F2040] max-w-[250px] truncate">
                      {e.estimate_name}
                      {onDrill && e.id && <span className="ml-1.5 text-[9px] font-semibold text-[#4ABFCF]" data-testid={`est-row-drill-${i}`}>Deal ›</span>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{e.organization_name || '—'}</td>
                    <td className="px-3 py-2.5">
                      <Badge variant="outline" className="text-[9px]">{e.implementation_type}</Badge>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge variant="secondary" className="text-[9px] capitalize">{e.complexity || '—'}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium">{e.total_effort} hrs</td>
                    <td className="px-4 py-2.5 text-right font-medium" data-testid={`est-row-cost-${e.id || e.estimate_id || ''}`}>{rowCost(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function ComparisonsDashboard({ data, groupBy }) {
  const { kpis, chart_data, comparisons } = data;
  const groupLabel = GROUP_OPTIONS.find(o => o.value === groupBy)?.label || groupBy;

  return (
    <div className="space-y-5" data-testid="comparisons-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Hash} label="Comparisons" value={kpis.count} />
        <KpiCard icon={DollarSign} label="Avg Cost Delta"
          value={`${kpis.avg_cost_delta >= 0 ? '+' : ''}$${kpis.avg_cost_delta.toLocaleString()}`}
          trend={kpis.avg_cost_delta_pct}
          color={kpis.avg_cost_delta > 0 ? 'text-red-600' : kpis.avg_cost_delta < 0 ? 'text-emerald-600' : 'text-[#0F2040]'} />
        <KpiCard icon={Clock} label="Avg Effort Delta"
          value={`${kpis.avg_effort_delta >= 0 ? '+' : ''}${kpis.avg_effort_delta} hrs`}
          color={kpis.avg_effort_delta > 0 ? 'text-red-600' : kpis.avg_effort_delta < 0 ? 'text-emerald-600' : 'text-[#0F2040]'} />
        <KpiCard icon={BarChart3} label="Avg Cost Change" value={`${kpis.avg_cost_delta_pct}%`}
          color={kpis.avg_cost_delta_pct > 0 ? 'text-red-600' : kpis.avg_cost_delta_pct < 0 ? 'text-emerald-600' : 'text-[#0F2040]'} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Cost Delta Chart */}
        <Card data-testid="chart-cost-delta">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">
              Avg Cost Delta — {groupLabel}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chart_data.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chart_data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `A$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`A$${Number(v).toLocaleString()}`, '']} />
                  <Bar dataKey="avg_cost_delta" name="Avg Cost Delta" radius={[4, 4, 0, 0]}>
                    {chart_data.map((d) => (
                      <Cell key={`cost-delta-${d.name}`} fill={d.avg_cost_delta >= 0 ? '#EF4444' : '#10B981'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        {/* Effort Delta Chart */}
        <Card data-testid="chart-effort-delta">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">
              Avg Effort Delta — {groupLabel}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chart_data.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chart_data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}h`} />
                  <Tooltip formatter={(v) => [`${Number(v).toLocaleString()} hrs`, '']} />
                  <Bar dataKey="avg_effort_delta" name="Avg Effort Delta" radius={[4, 4, 0, 0]}>
                    {chart_data.map((d) => (
                      <Cell key={`effort-delta-${d.name}`} fill={d.avg_effort_delta >= 0 ? '#EF4444' : '#10B981'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>
      </div>

      {/* Comparisons Table */}
      <Card data-testid="comparisons-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#0F2040]">
            Comparison Details ({comparisons.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Comparison</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Organization</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600">Base Cost</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600">Compare Cost</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600">Delta</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600">Base Effort</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600">Compare Effort</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Delta</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.slice(0, 50).map((c, i) => (
                  <tr key={c.id || `comp-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    data-testid={`comp-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium text-[#0F2040] max-w-[200px] truncate">{c.comparison_name}</td>
                    <td className="px-3 py-2.5 text-slate-600">{c.organization_name || '—'}</td>
                    <td className="px-3 py-2.5 text-right">${c.base_cost.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right">${c.compare_cost.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right">
                      <DeltaCell value={c.cost_delta} prefix="$" />
                    </td>
                    <td className="px-3 py-2.5 text-right">{c.base_effort} hrs</td>
                    <td className="px-3 py-2.5 text-right">{c.compare_effort} hrs</td>
                    <td className="px-4 py-2.5 text-right">
                      <DeltaCell value={c.effort_delta} suffix=" hrs" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {comparisons.length === 0 && (
            <div className="relative overflow-hidden text-center py-8 text-sm text-slate-400" data-testid="insights-comparisons-empty">
              <img
                src="/seertech-mark.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 opacity-[0.06]"
              />
              <span className="relative">No saved comparisons yet. Create comparisons from the Compare page.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function ApprovalsDashboard({ data }) {
  const { kpis, status_breakdown, response_time_by_estimate, top_stakeholders } = data;

  const formatHours = (h) => {
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 24) return `${Math.round(h)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };

  return (
    <div className="space-y-5" data-testid="approvals-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Send} label="Total Sent" value={kpis.total_sent} color="text-[#4ABFCF]" />
        <KpiCard icon={Check} label="Approval Rate" value={`${kpis.approval_rate}%`}
          sub={`${kpis.approved} approved`} color="text-emerald-600" />
        <KpiCard icon={Clock} label="Avg Response Time" value={formatHours(kpis.avg_response_hours)}
          sub={`${kpis.avg_response_hours}h`} color="text-[#0F2040]" />
        <KpiCard icon={AlertTriangle} label="Overdue" value={kpis.overdue}
          sub={`${kpis.pending} pending`} color={kpis.overdue > 0 ? 'text-red-600' : 'text-slate-400'} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status Breakdown Donut */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Approval Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {kpis.total_sent === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={status_breakdown} dataKey="count" nameKey="status" cx="50%" cy="50%"
                    innerRadius={60} outerRadius={90} paddingAngle={2} label={({ status, count }) => count > 0 ? `${status}: ${count}` : ''}>
                    {status_breakdown.map((entry) => (
                      <Cell key={`status-${entry.status}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} stakeholders`, name]} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="flex justify-center gap-4 mt-2">
              {status_breakdown.map(s => (
                <div key={s.status} className="flex items-center gap-1.5 text-xs">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="text-slate-600">{s.status}</span>
                  <span className="font-semibold text-[#0F2040]">{s.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Avg Response Time by Estimate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Avg Response Time by Estimate</CardTitle>
          </CardHeader>
          <CardContent>
            {response_time_by_estimate.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={response_time_by_estimate} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} label={{ value: 'Hours', position: 'insideBottomRight', offset: -5, fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} />
                  <Tooltip formatter={(value) => [`${value}h`, 'Avg Response']} />
                  <Bar dataKey="avg_hours" radius={[0, 4, 4, 0]}>
                    {response_time_by_estimate.map((r, idx) => (
                      <Cell key={`resp-${r.name || r.id || idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Responsive Stakeholders */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
            <Users className="w-4 h-4 text-[#4ABFCF]" /> Most Responsive Stakeholders
          </CardTitle>
        </CardHeader>
        <CardContent>
          {top_stakeholders.length === 0 ? (
            <p className="text-xs text-slate-400 py-4 text-center">No response data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="top-stakeholders-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 text-slate-500 font-semibold">#</th>
                    <th className="text-left py-2 px-2 text-slate-500 font-semibold">Stakeholder</th>
                    <th className="text-left py-2 px-2 text-slate-500 font-semibold">Email</th>
                    <th className="text-right py-2 px-2 text-slate-500 font-semibold">Avg Response</th>
                    <th className="text-right py-2 px-2 text-slate-500 font-semibold">Responses</th>
                  </tr>
                </thead>
                <tbody>
                  {top_stakeholders.map((s, i) => (
                    <tr key={s.email} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-2 px-2">
                        {i < 3 ? (
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${
                            i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-slate-400' : 'bg-amber-600'
                          }`}>{i + 1}</span>
                        ) : <span className="text-slate-400 pl-1">{i + 1}</span>}
                      </td>
                      <td className="py-2 px-2 font-semibold text-[#0F2040]">{s.name}</td>
                      <td className="py-2 px-2 text-slate-400">{s.email}</td>
                      <td className="py-2 px-2 text-right font-semibold text-[#0F2040]">{formatHours(s.avg_hours)}</td>
                      <td className="py-2 px-2 text-right">
                        <Badge variant="outline" className="text-[9px]">{s.responses}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function DeltaCell({ value, prefix = '', suffix = '' }) {
  if (!value && value !== 0) return <span className="text-slate-400">—</span>;
  const isUp = value > 0;
  const color = value === 0 ? 'text-slate-500' : isUp ? 'text-red-600' : 'text-emerald-600';
  return (
    <span className={`font-medium ${color}`}>
      {isUp ? '+' : ''}{prefix}{Math.abs(value).toLocaleString()}{suffix}
    </span>
  );
}


function EmptyChart() {
  return (
    <div className="flex items-center justify-center h-[220px] text-sm text-slate-400">
      <LayoutDashboard className="w-5 h-5 mr-2 opacity-50" />
      No data for current filters
    </div>
  );
}


// ── Dashboard-level Contingency What-If ────────────────────────────
// Applies a ±% multiplier to every cost/effort figure in the Estimates
// Dashboard view (KPIs, charts, role breakdown, table). View-only: does
// not touch stored data or AI narrative context.
const DASHBOARD_CONTINGENCY_PRESETS = [-10, 0, 5, 10, 20];
const DASHBOARD_CONTINGENCY_MIN = -20;
const DASHBOARD_CONTINGENCY_MAX = 100;

function applyContingencyToEstimatesData(data, pct) {
  const m = 1 + pct / 100;
  const scale = (v) => (typeof v === 'number' ? v * m : v);
  const scaleRecord = (obj) => {
    if (!obj) return obj;
    const out = {};
    Object.entries(obj).forEach(([k, v]) => { out[k] = scale(v); });
    return out;
  };
  return {
    ...data,
    kpis: data.kpis ? {
      ...data.kpis,
      avg_cost: Math.round(scale(data.kpis.avg_cost)),
      total_cost: Math.round(scale(data.kpis.total_cost)),
      avg_effort: Math.round(scale(data.kpis.avg_effort)),
      total_effort: Math.round(scale(data.kpis.total_effort)),
    } : data.kpis,
    chart_data: (data.chart_data || []).map(g => ({
      ...g,
      avg_cost: scale(g.avg_cost),
      avg_effort: scale(g.avg_effort),
      effort_by_role: scaleRecord(g.effort_by_role),
    })),
    role_breakdown: (data.role_breakdown || []).map(r => ({
      ...r,
      hours: Math.round(scale(r.hours)),
    })),
    estimates: (data.estimates || []).map(e => ({
      ...e,
      total_effort: Math.round(scale(e.total_effort)),
      total_cost: Math.round(scale(e.total_cost)),
    })),
  };
}

/**
/**
 * Custom Recharts <LabelList> renderer for per-group blended-rate deltas
 * on the Cost Distribution chart. Renders a small pill above each bar
 * with the group's blended rate vs portfolio average — emerald when the
 * group is below avg (good pricing), rose when above. Skipped silently
 * for groups where the delta couldn't be computed (e.g. zero effort).
 */
function renderBlendedDeltaLabel({ x, y, width, value }) {
  const v = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(v)) return null;
  const cx = x + width / 2;
  const fill = v > 0.5 ? '#E11D48' : v < -0.5 ? '#059669' : '#94A3B8';
  const sign = v > 0 ? '+' : '';
  return (
    <text
      x={cx}
      y={y - 6}
      fill={fill}
      fontSize={10}
      fontWeight={600}
      textAnchor="middle"
      data-testid="bar-blended-delta"
    >
      {sign}{v.toFixed(0)}%
    </text>
  );
}



/**
 * Renders the "vs portfolio average" delta chip beneath the Insights →
 * Estimates Dashboard Blended Rate KPI. Compares the current filter
 * selection's blended rate to the unfiltered portfolio average.
 *
 * Hidden when the benchmark is still loading, the portfolio sample is
 * too small to be meaningful (< 3 estimates), or the filtered selection
 * has zero effort.
 *
 * Note: when the user has the same filter selection as the whole
 * portfolio, the delta will be near 0 — the chip simply shows "on par
 * with portfolio avg" rather than misleading.
 */
function FilteredBlendedRateBenchmarkChip({ filteredBlended, benchmark }) {
  if (!benchmark || !benchmark.portfolio_blended_rate || benchmark.sample_size < 3 || !filteredBlended) {
    return <div className="text-[10px] text-slate-400 mt-0.5">Σ cost ÷ Σ effort</div>;
  }
  const portfolio = benchmark.portfolio_blended_rate;
  const deltaPct = ((filteredBlended - portfolio) / portfolio) * 100;
  const sign = deltaPct > 0 ? '↑' : deltaPct < 0 ? '↓' : '·';
  const direction = deltaPct > 0.5 ? 'above' : deltaPct < -0.5 ? 'below' : 'on par with';
  const tone = deltaPct > 0.5
    ? 'text-rose-600'
    : deltaPct < -0.5
      ? 'text-emerald-600'
      : 'text-slate-500';
  const portfolioRounded = Math.round(portfolio).toLocaleString();
  const absPct = Math.abs(deltaPct).toFixed(1);
  return (
    <div className="text-[10px] mt-0.5 leading-tight" data-testid="kpi-blended-rate-portfolio-chip">
      <span className={`font-semibold ${tone}`}>{sign} {absPct}%</span>
      <span className="text-slate-400"> {direction} ${portfolioRounded}/hr portfolio avg</span>
    </div>
  );
}


// ── Pricing Outliers Insights Card ─────────────────────────────────
// Auto-flags groups whose blended rate deviates ≥ OUTLIER_THRESHOLD_PCT
// from the unfiltered portfolio average and translates the chart's
// per-bar deltas into one-line action prompts a sales leader can act on.
const OUTLIER_THRESHOLD_PCT = 15;
const OUTLIER_HIGH_THRESHOLD_PCT = 30;
const OUTLIER_MIN_GROUP_SIZE = 2;
const OUTLIER_MAX_ROWS = 5;

function pricingOutlierPrompt(deltaPct) {
  if (deltaPct >= OUTLIER_HIGH_THRESHOLD_PCT) {
    return { tone: 'rose', label: 'High pushback risk — verify scope value-add.' };
  }
  if (deltaPct >= OUTLIER_THRESHOLD_PCT) {
    return { tone: 'rose', label: 'Review for sales pushback risk.' };
  }
  if (deltaPct <= -OUTLIER_HIGH_THRESHOLD_PCT) {
    return { tone: 'amber', label: 'Margin pressure — check for missing components.' };
  }
  if (deltaPct <= -OUTLIER_THRESHOLD_PCT) {
    return { tone: 'emerald', label: 'Investigate possible scope under-pricing.' };
  }
  return null;
}

function PricingOutliersCard({ chartData, portfolioBenchmark, groupLabel }) {
  const portfolioBlended = portfolioBenchmark?.portfolio_blended_rate || 0;
  const portfolioSample = portfolioBenchmark?.sample_size || 0;
  const ready = portfolioBlended > 0 && portfolioSample >= 3;

  const outliers = useMemo(() => {
    if (!ready || !Array.isArray(chartData) || chartData.length === 0) return [];
    const rows = [];
    chartData.forEach((g) => {
      const eff = Number(g.avg_effort || 0);
      const cost = Number(g.avg_cost || 0);
      const cnt = Number(g.count || 0);
      if (eff <= 0 || cnt < OUTLIER_MIN_GROUP_SIZE) return;
      const groupBlended = cost / eff;
      const deltaPct = ((groupBlended - portfolioBlended) / portfolioBlended) * 100;
      if (Math.abs(deltaPct) < OUTLIER_THRESHOLD_PCT) return;
      rows.push({
        name: g.name,
        count: cnt,
        groupBlended,
        deltaPct,
        groupTotalCost: cost * cnt,
      });
    });
    return rows.sort((a, b) => Math.abs(b.deltaPct) - Math.abs(a.deltaPct)).slice(0, OUTLIER_MAX_ROWS);
  }, [chartData, ready, portfolioBlended]);

  if (!ready) return null;

  return (
    <Card className="border-slate-200" data-testid="pricing-outliers-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Pricing Outliers — {groupLabel}
          </span>
          <span className="text-[10px] font-normal text-slate-400">
            ≥ ±{OUTLIER_THRESHOLD_PCT}% vs ${Math.round(portfolioBlended).toLocaleString()}/hr portfolio avg
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        {outliers.length === 0 ? (
          <div
            className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50/60 border border-emerald-100 rounded-md px-3 py-2"
            data-testid="pricing-outliers-empty"
          >
            <Check className="w-3.5 h-3.5" />
            All groups priced within ±{OUTLIER_THRESHOLD_PCT}% of the portfolio average.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {outliers.map((o) => {
              const prompt = pricingOutlierPrompt(o.deltaPct);
              const sign = o.deltaPct > 0 ? '+' : '';
              const pctTone = o.deltaPct > 0 ? 'text-rose-600 bg-rose-50' : 'text-emerald-600 bg-emerald-50';
              return (
                <li
                  key={`outlier-${o.name}`}
                  className="py-2.5 flex items-start gap-3"
                  data-testid={`pricing-outlier-row-${o.name}`}
                >
                  <span className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-bold tabular-nums ${pctTone}`}>
                    {sign}{o.deltaPct.toFixed(1)}%
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#0F2040] truncate">{o.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {o.count} estimate{o.count === 1 ? '' : 's'} · ${Math.round(o.groupBlended).toLocaleString()}/hr · ${Math.round(o.groupTotalCost).toLocaleString()} group total
                    </div>
                    {prompt && (
                      <div
                        className={`text-[11px] font-medium mt-1 ${
                          prompt.tone === 'rose' ? 'text-rose-600'
                            : prompt.tone === 'amber' ? 'text-amber-600'
                              : 'text-emerald-600'
                        }`}
                      >
                        {prompt.label}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}





function DashboardContingencyBar({ value, onChange, baselineTotalCost, baselineTotalEffort }) {
  const active = value !== 0;
  const deltaCost = (baselineTotalCost * value) / 100;
  const deltaEffort = (baselineTotalEffort * value) / 100;
  const sign = value > 0 ? '+' : '';

  return (
    <Card
      className={`border-dashed transition-colors ${
        active ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-slate-50/40'
      }`}
      data-testid="dashboard-contingency-bar"
    >
      <CardContent className="py-3 px-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5">
          {/* Label + presets */}
          <div className="flex items-center gap-2 shrink-0">
            <SlidersHorizontal className={`w-4 h-4 ${active ? 'text-amber-500' : 'text-slate-400'}`} />
            <div>
              <div className="text-xs font-semibold text-[#0F2040] flex items-center gap-1.5">
                What-if Contingency
                <span className="inline-flex items-center gap-0.5 text-[9px] font-normal text-slate-400 uppercase tracking-wide">
                  <Eye className="w-2.5 h-2.5" /> Display only
                </span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                Apply a portfolio-wide buffer across every estimate in view
              </div>
            </div>
          </div>

          {/* Preset chips */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="contingency-presets">
            {DASHBOARD_CONTINGENCY_PRESETS.map(p => {
              const selected = p === value;
              return (
                <button
                  key={`preset-${p}`}
                  type="button"
                  onClick={() => onChange(p)}
                  className={`h-7 px-2.5 rounded-full text-[11px] font-medium transition-all border ${
                    selected
                      ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300 hover:text-amber-700'
                  }`}
                  data-testid={`contingency-preset-${p}`}
                >
                  {p > 0 ? `+${p}%` : p === 0 ? 'Off' : `${p}%`}
                </button>
              );
            })}
          </div>

          {/* Slider for custom values */}
          <div className="flex items-center gap-3 flex-1 min-w-[200px]">
            <Slider
              min={DASHBOARD_CONTINGENCY_MIN}
              max={DASHBOARD_CONTINGENCY_MAX}
              step={1}
              value={[value]}
              onValueChange={(v) => onChange(v[0])}
              className="flex-1"
              data-testid="contingency-slider"
            />
            <div className="shrink-0 w-16 text-right">
              <span className={`text-sm font-bold tabular-nums ${active ? 'text-amber-600' : 'text-slate-400'}`}>
                {sign}{value}%
              </span>
            </div>
          </div>

          {/* Applied delta + reset */}
          {active && (
            <div className="flex items-center gap-3 pl-2 lg:border-l lg:border-amber-200" data-testid="contingency-applied">
              <div className="text-right">
                <div className={`text-[10px] font-semibold uppercase tracking-wide ${value > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                  {value > 0 ? 'Added' : 'Rebated'}
                </div>
                <div className="text-[11px] font-bold text-[#0F2040] tabular-nums">
                  {sign}${Math.round(Math.abs(deltaCost)).toLocaleString()} · {sign}{Math.round(Math.abs(deltaEffort)).toLocaleString()} hrs
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onChange(0)}
                className="h-7 px-2 text-[10px] text-slate-500 hover:text-slate-700"
                data-testid="contingency-reset"
              >
                Reset
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


const ESTIMATES_SECTIONS = [
  { key: 'portfolio_overview', label: 'Portfolio Overview', color: 'text-[#0F2040]' },
  { key: 'trends_and_patterns', label: 'Trends & Patterns', color: 'text-indigo-600' },
  { key: 'risk_flags', label: 'Risk Flags', color: 'text-red-500' },
  { key: 'optimization_opportunities', label: 'Optimization Opportunities', color: 'text-emerald-600' },
];

const COMPARISONS_SECTIONS = [
  { key: 'comparison_summary', label: 'Comparison Summary', color: 'text-[#0F2040]' },
  { key: 'decision_patterns', label: 'Decision Patterns', color: 'text-indigo-600' },
  { key: 'risk_and_feasibility', label: 'Risk & Feasibility', color: 'text-red-500' },
  { key: 'strategic_insights', label: 'Strategic Insights', color: 'text-emerald-600' },
];

function InsightsNarrativePanel({ narrative, loading, stale, onGenerate, dashboardType }) {
  const sections = dashboardType === 'estimates' ? ESTIMATES_SECTIONS : COMPARISONS_SECTIONS;

  return (
    <Card className="border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white" data-testid="insights-narrative-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">AI Insights</CardTitle>
            {stale && !loading && (
              <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-600 bg-amber-50 animate-pulse">
                Filters changed — refresh for updated insights
              </Badge>
            )}
          </div>
          <Button
            variant={stale ? 'default' : 'outline'}
            size="sm"
            onClick={onGenerate}
            disabled={loading}
            className={stale
              ? 'bg-amber-500 hover:bg-amber-600 text-white text-xs h-8 px-4 shadow-sm'
              : 'text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-50'}
            data-testid="generate-narrative-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            {loading ? 'Analyzing...' : stale ? 'Refresh AI Insights' : 'Regenerate'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && !narrative && (
          <div className="flex items-center justify-center py-10 gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
            <span className="text-sm text-slate-500">Generating AI insights from your dashboard data...</span>
          </div>
        )}
        {narrative && (
          <div className={`space-y-4 text-[12px] text-slate-700 leading-relaxed ${loading ? 'opacity-50' : ''}`}>
            {sections.map(s => narrative[s.key] && (
              <div key={s.key}>
                <p className={`text-[10px] font-bold uppercase tracking-wide mb-1 ${s.color}`}>{s.label}</p>
                <p className="whitespace-pre-line">{narrative[s.key]}</p>
              </div>
            ))}
            {narrative.recommendations && narrative.recommendations.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide mb-1 text-violet-600">Recommendations</p>
                <ul className="space-y-1.5 ml-1">
                  {narrative.recommendations.map((r, i) => (
                    <li key={`rec-${i}`} className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-600 text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {!narrative && !loading && (
          <div className="text-center py-8 text-sm text-slate-400">
            Click "Regenerate" to generate AI insights for this dashboard view.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
